import {
  Injectable,
  inject,
  signal,
  computed,
  effect,
  untracked,
  EnvironmentInjector,
  runInInjectionContext,
} from "@angular/core";
import { toSignal, toObservable } from "@angular/core/rxjs-interop";
import { combineLatest, of, switchMap } from "rxjs";
import {
  Database,
  ref,
  objectVal,
  listVal,
  update,
  push,
  remove,
  set,
  get,
  onValue,
  runTransaction,
  serverTimestamp,
} from "@angular/fire/database";
import { defaults } from "../shared/defaults";
import { isRebuyCapable } from "../shared/tournament-type.util";
import { UtilService, localDateTimeToTs } from "./util.service";
import { TrackingService } from "./tracking.service";
import { AuthService } from "./auth.service";
import { ProfileService } from "./profile.service";
import { ClubService } from "./club.service";
import { StorageService } from "./storage.service";
import { ApiService } from "./api.service";
import { TranslateService } from "@ngx-translate/core";
import { BvDialogService, BvToastService } from "../design-system";
import { AlertDialogComponent } from "../shared/alert-dialog/alert-dialog.component";
import { ConfirmDialogComponent } from "../shared/confirm-dialog/confirm-dialog.component";
import { Subject } from "rxjs";
import { AudioService } from "./audio.service";
import { LeagueService } from "./league.service";
import * as math from "mathjs/dist/math";
import { PlayerMovesComponent } from "../shared/player-moves/player-moves.component";
import { ClockOverlayService } from "./clock-overlay.service";
import { BountyRevealDialogComponent } from "../tournament/bounty-reveal/bounty-reveal-dialog.component";
import { RoutingService } from "./routing.service";
import { MaxPlayersComponent } from "../shared/max-players/max-players.component";
import { deriveTournStatus } from "./tourn-status";

@Injectable({ providedIn: "root" })
export class TournamentService {
  private readonly mdb = inject(Database); // modular — read signals
  private readonly injector = inject(EnvironmentInjector);
  private readonly storageServ = inject(StorageService);

  /** Active tournament id. `init()` sets it; the read signals below re-key. */
  readonly selectedTournId = signal<string | null>(null);
  // Lazy log loading: the log stream only listens once a consumer opens the
  // log tab via initLogs() (logs can be large). (Was the on-demand log sub.)
  private readonly logEnabled = signal(false);

  // --- Live tournament data (modular AngularFire, keyed on selectedTournId) ---
  private readonly tournamentSig = toSignal(
    toObservable(this.selectedTournId).pipe(
      switchMap((id) =>
        id
          ? runInInjectionContext(this.injector, () =>
              objectVal<any>(ref(this.mdb, `tournaments/${id}`)),
            )
          : of(null),
      ),
    ),
    { initialValue: null as any },
  );
  private readonly tablesSig = toSignal(
    toObservable(this.selectedTournId).pipe(
      switchMap((id) =>
        id
          ? runInInjectionContext(this.injector, () =>
              listVal<any>(ref(this.mdb, `tournaments/${id}/tables`)),
            )
          : of([]),
      ),
    ),
    { initialValue: [] as any[] },
  );
  private readonly tableNamesSig = toSignal(
    toObservable(this.selectedTournId).pipe(
      switchMap((id) =>
        id
          ? runInInjectionContext(this.injector, () =>
              listVal<any>(ref(this.mdb, `tournaments/${id}/tableNames`)),
            )
          : of([]),
      ),
    ),
    { initialValue: [] as any[] },
  );
  private readonly levelsSig = toSignal(
    toObservable(this.selectedTournId).pipe(
      switchMap((id) =>
        id
          ? runInInjectionContext(this.injector, () =>
              listVal<any>(ref(this.mdb, `structures/${id}/levels`), {
                keyField: "key",
              }),
            )
          : of([]),
      ),
    ),
    { initialValue: [] as any[] },
  );
  private readonly payoutsSig = toSignal(
    toObservable(this.selectedTournId).pipe(
      switchMap((id) =>
        id
          ? runInInjectionContext(this.injector, () =>
              listVal<any>(ref(this.mdb, `payouts/${id}`)),
            )
          : of([]),
      ),
    ),
    { initialValue: [] as any[] },
  );
  private readonly structuresSig = toSignal(
    toObservable(this.selectedTournId).pipe(
      switchMap((id) =>
        id
          ? runInInjectionContext(this.injector, () =>
              objectVal<any>(ref(this.mdb, `structures/${id}`)),
            )
          : of(null),
      ),
    ),
    { initialValue: null as any },
  );
  // Lazy: only listens after initLogs() flips logEnabled.
  private readonly logSig = toSignal(
    toObservable(
      computed(() => (this.logEnabled() ? this.selectedTournId() : null)),
    ).pipe(
      switchMap((id) =>
        id
          ? runInInjectionContext(this.injector, () =>
              listVal<any>(ref(this.mdb, `logs/tournaments/${id}`), {
                keyField: "key",
              }),
            )
          : of([]),
      ),
    ),
    { initialValue: [] as any[] },
  );

  // --- Split-parent child subscriptions ---
  // Keyed on a STABLE string of child IDs: tournamentSig emits a new object on
  // every live tick, so switching on the raw splitInto object would tear down
  // and re-subscribe every child stream on unrelated tournament updates.
  private readonly splitChildIdsKey = computed(() => {
    const s = this.tournamentSig()?.splitInto;
    return s ? Object.keys(s).sort().join(",") : "";
  });
  private readonly splitChildrenSig = toSignal(
    toObservable(this.splitChildIdsKey).pipe(
      switchMap((key) =>
        key
          ? runInInjectionContext(this.injector, () =>
              combineLatest(
                key
                  .split(",")
                  .map((id) =>
                    objectVal<any>(ref(this.mdb, `tournaments/${id}`)),
                  ),
              ),
            )
          : of([] as any[]),
      ),
    ),
    { initialValue: [] as any[] },
  );

  // External consumers (clock.component) subscribe to this — bridge the signal.
  readonly tournament = toObservable(this.tournamentSig);

  // --- Legacy field getters (consumers unchanged) ---
  get latestTourn() {
    return this.tournamentSig();
  }
  get latestLive() {
    return this.tournamentSig()?.live;
  }
  get latestTables() {
    return this.tablesSig();
  }
  get tableNames() {
    return this.tableNamesSig();
  }
  // Continuous table numbering across a split night: the server stamps each
  // child with the number of tables in the children before it, so 1/4 has
  // tables 1-2, 2/4 has 3-4, ... Tables keep local numbers 1..k in the data.
  get tableNumberOffset(): number {
    return this.latestTourn?.tableNumberOffset || 0;
  }
  // The ONE way to name a table for display. Custom name wins, then the
  // final-table label, then "<Table> N" with N offset for split children.
  // opts lets hub views label another tournament's tables.
  tableLabel(
    table: any,
    opts?: { names?: any; offset?: number },
  ): string {
    const names = opts?.names ?? this.tableNames;
    const n = Number(table?.number) || 0;
    const custom = names && names[n - 1];
    if (custom) return custom;
    if (table?.finalTable) return this.translate.instant("FINAL_TABLE");
    const offset = opts?.offset ?? this.tableNumberOffset;
    return this.translate.instant("TABLE") + " " + (n + offset);
  }
  // Same resolution for contexts that already print the word "Table"
  // (players list, move rows, seated toasts): custom name or the global number.
  tableNumberDisplay(
    n: number,
    opts?: { names?: any; offset?: number },
  ): string {
    const names = opts?.names ?? this.tableNames;
    const custom = names && names[n - 1];
    if (custom) return custom;
    const offset = opts?.offset ?? this.tableNumberOffset;
    return String(n + offset);
  }
  get latestLevels() {
    return this.levelsSig();
  }
  get latestPayouts() {
    return this.payoutsSig();
  }
  get latestLog() {
    return this.logSig();
  }
  // Server-aligned wall clock for the synced bounty reveal. Reuses the proven
  // clock-sync delta (`Date.now() - delta` ≈ server time) — the same correction
  // the live clock uses across devices — so the reveal lands at the same instant
  // everywhere. The clockOverlay node's `timestamp` is a server timestamp, so
  // they're directly comparable.
  clockNow(): number {
    return Date.now() - this.deltaSig();
  }
  get tournID(): string {
    return this.selectedTournId() as string;
  }
  get struct(): boolean {
    return !!this.structuresSig()?.levels;
  }
  // Canonical bounty-mode read. The field is `params.bountyMode`
  // ("none" | "fixed" | "mystery"); for legacy tournaments that predate the
  // enum it falls back to the old `bountyTournament` boolean. Use these getters
  // everywhere instead of reading the raw fields so the fallback stays in one
  // place (and a future "progressive" mode slots in here too).
  get bountyMode(): "none" | "fixed" | "mystery" {
    const p = this.latestTourn?.params;
    if (!p) return "none";
    if (p.bountyMode) return p.bountyMode;
    return p.bountyTournament ? "fixed" : "none";
  }
  get isFixedBounty(): boolean {
    return this.bountyMode === "fixed";
  }
  get isMysteryBounty(): boolean {
    return this.bountyMode === "mystery";
  }
  // Progressive Knockout is stored as the Fixed style + a `progressive` flag
  // (so all Fixed funding/carve/registration code is reused). The KO splits the
  // victim's head: half grows the killer's own head, the rest is cash.
  get isProgressiveBounty(): boolean {
    return (
      this.bountyMode === "fixed" && !!this.latestTourn?.params?.progressive
    );
  }
  // Individual bounties live on `player.bountyValue` in a None-mode tournament
  // (staff put a bounty on a specific head via Add Bounty). True once any player
  // has a bounty on their head OR has collected one — used to surface the
  // Bounties tab / column / leaderboard in an otherwise non-bounty tournament.
  get hasIndividualBounties(): boolean {
    return (this.latestPlayers || []).some(
      (p: any) => (p?.bountyValue || 0) > 0 || (p?.bountyWinnings || 0) > 0,
    );
  }
  get showBounties(): boolean {
    return this.bountyMode !== "none" || this.hasIndividualBounties;
  }
  // --- Split tournament (parent = control hub, children = the real fields) ---
  // Swap position is off for shootouts (places come from table-winner
  // rounds) and Split parents (results live in the children).
  get swapFinishUnavailable(): boolean {
    return (
      this.isSplitParent ||
      this.latestTourn?.params?.tournamentType === "shootout"
    );
  }
  get isSplitParent(): boolean {
    return !!this.latestTourn?.splitInto;
  }
  get isSplitChild(): boolean {
    return !!this.latestTourn?.splitFrom;
  }
  get splitChildCount(): number {
    const meta = this.latestTourn?.splitInto;
    return meta ? Object.keys(meta).length : 0;
  }
  // Clock mode chosen at split time: "synchronized" (the parent hub drives
  // one lockstep clock) or "independent" (each child runs its own clock).
  // Stamped on parent AND children at split, so both sides read it locally.
  get splitClockMode(): string {
    return this.latestTourn?.splitClockMode || "synchronized";
  }
  get isSyncedSplitParent(): boolean {
    return this.isSplitParent && this.splitClockMode === "synchronized";
  }
  get isSyncedSplitChild(): boolean {
    return this.isSplitChild && this.splitClockMode === "synchronized";
  }
  get isIndependentSplitParent(): boolean {
    return this.isSplitParent && this.splitClockMode === "independent";
  }
  // Client-side mirror of the server's split-undo refusal (any child with
  // activity blocks it) — drives the log's disabled undo button. Approximate:
  // child log entries aren't loaded here; the server remains the real guard.
  // The split itself seats the children (Seating + live.seated), so that is
  // the base state, not activity.
  get splitUndoBlocked(): boolean {
    if (!this.isSplitParent) return false;
    return this._splitChildren().some((c: any) => {
      const l = c.live || {};
      return (
        (c.tournStatus !== "Scheduled" && c.tournStatus !== "Seating") ||
        (l.out || 0) > 0 ||
        l.running ||
        !!l.started ||
        (l.seconds || 0) > 0 ||
        (l.undoIdx || 0) > 0
      );
    });
  }
  // Public tick pulse for components that derive their own clocks (the split
  // mini-clock dashboard): reading it inside a computed re-runs the
  // derivation every 500ms while the clock tab ticks.
  get clockTick(): number {
    return this.tickCounter();
  }
  // Which child the hub's rotating per-child widgets (players remaining /
  // average stack, and the whole clock face in independent mode) currently
  // show: 16s per child, every widget on the same child at the same moment.
  // Slot comes from the wall clock, not a tick count: tick() has more than one
  // driver (the fullscreen seats overlay runs its own interval), which made
  // counted ticks rotate 2x+ faster. Wall clock also keeps several screens in
  // the room in step. tickCounter() is read only to re-evaluate.
  get splitRotationChild(): any | null {
    const children = this._splitChildren();
    if (!children.length) return null;
    this.tickCounter();
    const idx = Math.floor(Date.now() / 16000) % children.length;
    return children[idx];
  }
  // Child tournament records of a Split parent, ordered by their 1..N index.
  // Just-deleted children resolve to null records and are dropped.
  private readonly _splitChildren = computed(() => {
    const meta = this.tournamentSig()?.splitInto;
    if (!meta) return [];
    return (this.splitChildrenSig() || [])
      .filter((c: any) => !!c?.tournID)
      .sort(
        (a: any, b: any) =>
          (meta[a.tournID]?.index || 0) - (meta[b.tournID]?.index || 0),
      );
  });
  get splitChildren(): any[] {
    return this._splitChildren();
  }
  // Live roster of a Split parent: the union of the children's players (the
  // parent's own players node is the frozen pre-split snapshot). Rows carry
  // their child so per-player ops can route there. null while the child
  // subscriptions are still loading (a split always has ≥2 children).
  private readonly _splitPlayers = computed(() => {
    const meta = this.tournamentSig()?.splitInto;
    if (!meta) return null;
    const children = this._splitChildren();
    if (!children.length) return null;
    // Positional labels over the CURRENT children (a deleted child would
    // leave holes in the stored 1..N indexes: "3/2" reads as nonsense).
    const total = children.length;
    const rows: any[] = [];
    for (let ci = 0; ci < children.length; ci++) {
      const child = children[ci];
      const label = `${ci + 1}/${total}`;
      for (const p of this.util.getObjArray(child.players || {})) {
        rows.push({
          ...p,
          _childTournID: child.tournID,
          _childIndex: label,
          _childName: child.tournName,
        });
      }
    }
    return rows;
  });
  get splitPlayers(): any[] | null {
    return this._splitPlayers();
  }
  // Tables to display, grouped by tournament. A Split parent shows every
  // child's tables (the children are already subscribed in full, tables
  // included) with each child's numbering offset; anything else is a single
  // unlabelled group of its own tables. Feeds the seats overlay (TV) and the
  // hub's seating tab.
  private readonly _seatingGroups = computed(() => {
    const t = this.tournamentSig();
    if (!t) return [] as any[];
    if (t.splitInto) {
      const children = this._splitChildren();
      const total = children.length;
      return children.map((child: any, ci: number) => ({
        tournID: child.tournID,
        label: child.tournName,
        indexLabel: `${ci + 1}/${total}`,
        status: child.tournStatus,
        offset: child.tableNumberOffset || 0,
        names: child.tableNames || null,
        tables: (child.tables || []).filter((tbl: any) => tbl && !tbl.broke),
        seated: !!child.live?.seated,
      }));
    }
    return [
      {
        tournID: t.tournID,
        label: null,
        indexLabel: null,
        status: t.tournStatus,
        offset: t.tableNumberOffset || 0,
        names: this.tableNamesSig(),
        tables: (this.tablesSig() || []).filter((tbl: any) => tbl && !tbl.broke),
        seated: !!t.live?.seated,
      },
    ];
  });
  get seatingGroups(): any[] {
    return this._seatingGroups();
  }
  // The hub has seats to show once any child is seated.
  get hasSplitSeating(): boolean {
    return this.isSplitParent && this._seatingGroups().some((g) => g.seated);
  }
  // Label a split-parent roster row's table with ITS child's names + offset.
  splitRowTableDisplay(row: any): string {
    const child = this._splitChildren().find(
      (c: any) => c.tournID === row?._childTournID,
    );
    return this.tableNumberDisplay(Number(row?.table) || 0, {
      names: child?.tableNames || null,
      offset: child?.tableNumberOffset || 0,
    });
  }
  // Player rows for clock info displays: a Split parent shows the union of
  // its children's players (rows carry _childTournID); everything else uses
  // latestPlayers.
  get infoPlayers(): any[] | null {
    return this.isSplitParent ? this.splitPlayers : this.latestPlayers;
  }
  // Bridge for consumers outside the signal graph (InfoBoxService): child
  // changes on a Split parent (eliminations, stacks) never touch the parent
  // node, so the parent's tournament stream alone won't re-emit.
  readonly splitChildren$ = toObservable(this._splitChildren);
  // Derived hub stats for the parent clock (its own live counters are frozen
  // at split time — children hold the real state).
  get splitRemainingCount(): number {
    return (this.splitPlayers || []).filter((p: any) => !p.finish).length;
  }
  get splitTotalChips(): number {
    return this._splitChildren().reduce(
      (sum: number, c: any) =>
        sum +
        (c.live?.totalChips ||
          Object.values(c.players || {}).reduce(
            (s: number, p: any) => s + (p.stack || 0), // stack already includes bonusChips
            0,
          )),
      0,
    );
  }
  // The individual bounty currently ON this player's head, for the avatar pill.
  // Only notable heads: still-active players, never Mystery (heads hidden), and
  // in Fixed only when overridden away from the uniform per-player value.
  individualBountyOf(player: any): number {
    if (!player || player.finish) return 0;
    if (this.bountyMode === "mystery") return 0;
    // Progressive: head = base per-player bounty + accumulated KO growth
    // (bountyValue holds only the growth). Show it on every active player, so at
    // the start everyone shows the base bounty per player.
    if (this.isProgressiveBounty) {
      return (
        (this.latestTourn?.params?.bountyPerPlayer || 0) +
        (player.bountyValue || 0)
      );
    }
    const v = player.bountyValue || 0;
    if (!v) return 0;
    // Uniform Fixed heads are all the same → noise; only show overrides.
    if (
      this.bountyMode === "fixed" &&
      v === (this.latestTourn?.params?.bountyPerPlayer || 0)
    )
      return 0;
    return v;
  }
  // Seating renders players from the tables snapshot taken at Draw Seats time,
  // so a bounty added AFTER the draw isn't on that copy. Resolve the LIVE player
  // record by uid for the pill.
  individualBountyForUid(uid: string): number {
    return this.individualBountyOf(this.latestTourn?.players?.[uid]);
  }
  // Mystery only: the prize pool freezes at the FIRST bounty DRAW, not the stage
  // trigger. Eliminating players and deferring every draw keeps the pool fully
  // editable; the lock engages once any prize has been drawn (a drawnBy is set)
  // and the server takes the pool snapshot. Derived (not a flag) so it reverts
  // automatically when the only draw is undone. Mirrors server bountyPoolLocked.
  get bountyPoolLocked(): boolean {
    if (this.bountyMode !== "mystery") return false;
    const prizes = this.latestTourn?.mysteryPool?.prizes;
    if (!prizes) return false;
    const arr = Array.isArray(prizes) ? prizes : Object.values(prizes);
    return arr.some((p: any) => p && p.drawnBy);
  }
  // Signed delta of the persisted MANUAL prize list vs the carved pool. 0 (or
  // auto mode) = balanced. Derived from params.bountyPrizes so it's available
  // everywhere, unlike the editor-only in-memory bountyDelta signal.
  get bountyPrizesDelta(): number {
    const p = this.latestTourn?.params;
    if (!p || this.bountyMode !== "mystery" || !p.bountyPrizesManual) return 0;
    if (!Array.isArray(p.bountyPrizes) || !p.bountyPrizes.length) return 0;
    // A manual list whose length no longer matches the prize count is IGNORED
    // by the pool builder (it auto-generates a balanced pool), so its delta
    // must not block draws — e.g. placesPaid changed after the list was
    // entered. Mirrors the server mysteryBountyDelta.
    if (p.bountyPrizes.length !== this.mysteryPrizeCount) return 0;
    const sum = p.bountyPrizes.reduce((a: number, b: number) => a + (b || 0), 0);
    return math.round(sum - (p.bountyPool || 0), 2);
  }
  // Draws are blocked while a manual prize delta exists and the pool isn't
  // frozen yet (after the first draw the pool is built + locked, params can no
  // longer change). Forces staff to reconcile the manual list (or switch to
  // auto) before the snapshot is taken.
  get bountyDrawBlocked(): boolean {
    return !this.bountyPoolLocked && this.bountyPrizesDelta !== 0;
  }
  // "From first elimination" sizes the bounty prize COUNT from the whole field,
  // so places-paid doesn't feed the pool and stays editable; ITM sizes the
  // count from places-paid, so places-paid must freeze with the pool too.
  get bountyImmediateStage(): boolean {
    const p = this.latestTourn?.params;
    return !!(p && p.mysteryImmediate && !isRebuyCapable(p.tournamentType));
  }
  get placesPaidLocked(): boolean {
    return this.bountyPoolLocked && !this.bountyImmediateStage;
  }
  get isShootout(): boolean {
    return this.latestTourn?.params?.tournamentType === "shootout";
  }
  get shootoutRound(): number {
    return this.latestLive?.shootoutRound || 1;
  }
  // Remaining bracket depth from the CURRENT round's table count: each round
  // re-deals ceil(T/perTable) tables (a set that fits the final-table size is
  // the final) until one table plays to the champion.
  get shootoutTotalRounds(): number {
    if (!this.isShootout || !this.latestLive?.seated) return 0;
    const tables = (this.latestTourn?.tables || []).filter(
      (tbl: any) => tbl && !tbl.broke,
    ).length;
    if (!tables) return 0;
    const p = this.latestTourn?.params || {};
    const perTable = Number(p.perTable) || 9;
    // Heads-up final is a match of 2 whatever the configured final-table
    // size says (server pins it the same way in the deal).
    const finalSize =
      perTable === 2 ? 2 : p.numberPlayersFinalTable || perTable;
    let rounds = this.shootoutRound;
    let winners = tables;
    while (winners > 1) {
      rounds++;
      winners = winners <= finalSize ? 1 : Math.ceil(winners / perTable);
    }
    return rounds;
  }
  // The lone seated player at a completed shootout table (null while the
  // table is still playing).
  shootoutTableWinner(tbl: any): any {
    if (!tbl || (tbl.count || 0) !== 1 || !Array.isArray(tbl.seats))
      return null;
    return (
      tbl.seats.find((s: any) => s && typeof s === "object" && s.uid) || null
    );
  }
  // Whether any player has busted yet. Derived from live.out so it reverts the
  // moment the only elimination is undone (used to gate bounty-mode switches).
  get eliminationsStarted(): boolean {
    return (this.latestLive?.out || 0) > 0;
  }
  // Log-undo types that resize the field / prize pool. Their undo is blocked
  // once the mystery pool is frozen (mirrors the server logUndo guard).
  // ELIMINATION / MYSTERY_DRAW are intentionally excluded — undoing those is
  // how the freeze reverts.
  private static readonly FIELD_CHANGING_UNDO_TYPES = [
    "REGISTER",
    "UNREGISTER",
    "REBUY",
    "ADDON",
    "REENTRY",
  ];
  undoBlockedByBountyPool(entry: any): boolean {
    return (
      this.bountyPoolLocked &&
      !!entry &&
      TournamentService.FIELD_CHANGING_UNDO_TYPES.indexOf(entry.type) !== -1
    );
  }
  // Params that move the total prize pool — rejected once the pool is frozen.
  // (places-paid is handled separately because it only locks in ITM mode.)
  private static readonly PRIZEPOOL_PARAMS = [
    "buyin",
    "addedPrize",
    "rebuyCost",
    "addonCost",
    "reentryCost",
    "actPlayers",
    "bountySplit",
    "managePayouts",
    "manageRegistrations",
    "bountyPerPlayer",
  ];
  private prizepoolParamLocked(param: string): boolean {
    if (!this.bountyPoolLocked) return false;
    if (TournamentService.PRIZEPOOL_PARAMS.indexOf(param) !== -1) return true;
    if (
      (param === "placesPaid" || param === "calcPlacesPaidManual") &&
      this.placesPaidLocked
    )
      return true;
    return false;
  }
  // Bounty a player has collected, shown separately from their prize-pool
  // payout. The authoritative source is the server-persisted bountyWinnings
  // (set on each knockout for fixed/individual/progressive, drawn/swept for
  // mystery). For fixed tournaments that pre-date persisted bountyWinnings (or
  // mid-flight), fall back to the deterministic kos × per-player value (+ own
  // bounty for the winner) so the column still renders. Use everywhere instead
  // of reading player.bountyWinnings directly.
  // Total bounty money. Mystery: the carved pool (params.bountyPool). Fixed:
  // per-player value × entries, computed live so it's correct even when payouts
  // aren't managed (in which case params.bountyPool is never persisted → 0).
  get bountyPoolTotal(): number {
    const p = this.latestTourn?.params;
    if (!p) return 0;
    if (this.isFixedBounty) {
      const per = p.bountyPerPlayer || 0;
      const entries = p.manageRegistrations
        ? this.latestTourn?.playerCount || 0
        : p.actPlayers || 0;
      // Individual overrides in fixed mode change the money on heads, so the
      // total must sum them (mirrors server calcBountyPool). Progressive is
      // excluded: bountyValue is KO growth (redistribution), not new money.
      if (!this.isProgressiveBounty) {
        const players = this.latestPlayers || [];
        if (players.length) {
          let sum = 0;
          for (const pl of players) {
            if (!pl) continue;
            sum += pl.bountyValue != null ? pl.bountyValue : per;
          }
          if (entries > players.length) sum += (entries - players.length) * per;
          return sum;
        }
      }
      return per * entries;
    }
    if (this.bountyMode === "none") {
      // Individual bounties: Σ per-player head values, computed live so it's
      // correct even when payouts aren't managed (params.bountyPool unwritten).
      return (this.latestPlayers || []).reduce(
        (s: number, pl: any) => s + (pl?.bountyValue || 0),
        0,
      );
    }
    return p.bountyPool || 0;
  }
  // Gross pot BEFORE the bounty carve (mirrors calcPrizepool's pre-carve sum).
  // Needed to show the bounty/placement split and detect over-funding: the
  // persisted placement pool (params.prizepool) is floored at 0, which hides
  // the deficit when bounties exceed the money collected.
  get grossPrizePool(): number {
    const p = this.latestTourn?.params;
    const live = this.latestLive;
    if (!p) return 0;
    const playerCount = p.manageRegistrations
      ? this.latestTourn?.playerCount || 0
      : p.actPlayers || 0;
    let pool = 0;
    if (p.buyin) {
      const reentries = (live && live.reentries) || 0;
      pool += p.manageRegistrations
        ? (playerCount - reentries) * p.buyin
        : playerCount * p.buyin;
    }
    if (isRebuyCapable(p.tournamentType) && live) {
      pool +=
        (live.rebuys || 0) * (p.rebuyCost || 0) +
        (live.addons || 0) * (p.addonCost || 0);
    }
    if (p.tournamentType === "reentry_tournament" && live && live.reentries) {
      pool += live.reentries * (p.reentryCost || 0);
    }
    pool += p.addedPrize || 0;
    return pool;
  }
  // Placement (finishing-place) pool = gross minus the bounty carve, floored 0.
  get placementPoolValue(): number {
    return Math.max(0, this.grossPrizePool - this.bountyPoolTotal);
  }
  // How much the bounties exceed the money collected. Only fixed/progressive/
  // individual can over-fund; mystery is a fraction of the pot so never does.
  get bountyOverfundBy(): number {
    if (this.bountyMode === "mystery") return 0;
    return Math.max(0, this.bountyPoolTotal - this.grossPrizePool);
  }
  get bountiesOverfunded(): boolean {
    return this.bountyOverfundBy > 0;
  }
  // Count of players currently carrying an individual bounty (Bounties tab box).
  get individualBountyCount(): number {
    return (this.latestPlayers || []).filter(
      (p: any) => (p?.bountyValue || 0) > 0,
    ).length;
  }
  bountyWon(player: any): number {
    if (!player) return 0;
    if (player.bountyWinnings != null) return player.bountyWinnings;
    if (this.isFixedBounty) {
      const per = this.latestTourn?.params?.bountyPerPlayer || 0;
      if (!per) return 0;
      return (player.kos || 0) * per + (player.finish === 1 ? per : 0);
    }
    return 0;
  }
  // One bounty prize per player alive at the stage trigger: ITM → places paid;
  // "from first elimination" (no-rebuy types) → the whole field. (Estimate
  // during setup; the authoritative count is frozen server-side at the
  // trigger.)
  get mysteryPrizeCount(): number {
    const p = this.latestTourn?.params;
    if (!p) return 0;
    if (p.mysteryImmediate && !isRebuyCapable(p.tournamentType)) {
      return p.manageRegistrations
        ? this.latestTourn?.playerCount || p.expPlayers || 0
        : p.actPlayers || 0;
    }
    return p.placesPaid || 0;
  }
  // Turn a bounty pool (P) + count (N) into the individual "ping-pong ball" prize
  // values. Model: a heavy modal FLOOR (~60% of the field at ~0.4·avg) plus a set
  // of equal-money premium tiers that grow triangularly (tier i holds i prizes),
  // so tier 1 is the single jackpot. Σvalue == P and length == N exactly, and the
  // same (P,N) is deterministic. (roundTo is ignored — the ladder owns denoms.)
  // MUST stay byte-for-logic in sync with the server bvapi.js generateBountyPrizes.
  generateBountyPrizes(pool: number, count: number, _roundTo = 1): number[] {
    const N = Math.floor(count);
    if (!(pool > 0) || !(N >= 1)) return [];

    // Low-stakes pools (avg < $5) compute in NICKELS so denominations can drop to
    // $0.05 (every value stays a multiple of $0.05) instead of being crushed to
    // $1; normal pools use whole dollars. All the integer math below runs in the
    // scaled unit; the results are scaled back to dollars on the way out.
    const scale = pool / N < 5 ? 20 : 1;
    const P = Math.round(pool * scale);
    const descale = (arr: number[]): number[] =>
      scale === 1 ? arr : arr.map((v) => v / scale);

    const LADDER = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 7, 8, 9];
    // Nearest/floor/ceil integer "casino" denomination ({1,1.5,2,2.5,3,4,5,6,7,8,9}×10^k).
    const nice = (x: number, mode: string): number => {
      if (!(x > 0)) return 1;
      const exp = Math.floor(Math.log10(x));
      let best = mode === "ceil" ? Infinity : 1;
      for (let e = Math.max(0, exp - 1); e <= exp + 2; e++) {
        const base = Math.pow(10, e);
        for (const mlt of LADDER) {
          const v = mlt * base;
          if (v !== Math.round(v)) continue; // integer denominations only
          if (mode === "floor" && v <= x + 1e-6) best = Math.max(best, v);
          else if (mode === "ceil" && v >= x - 1e-6) best = Math.min(best, v);
          else if (mode === "round" && Math.abs(v - x) < Math.abs(best - x))
            best = v;
        }
      }
      return best === Infinity ? Math.round(x) : best;
    };
    const niceRound = (x: number) => nice(x, "round");
    const niceFloor = (x: number) => nice(x, "floor");
    const niceCeil = (x: number) => nice(x, "ceil");

    if (N === 1) return descale([P]);
    if (N === 2) {
      // Two bounties: a 75/25 split — a little spice (one big, one small). Nice-
      // round the small (25%) so the big one absorbs the rest to an exact sum.
      const small = Math.min(niceRound(0.25 * P), P);
      return descale([P - small, small]);
    }
    if (N === 3) {
      // Three bounties: a 60/20/20 split — one jackpot + two equal smaller. Nice-
      // round the smalls (20% each); the big absorbs the rest for an exact sum.
      const small = Math.min(niceRound(0.2 * P), Math.floor(P / 3));
      return descale([P - 2 * small, small, small]);
    }
    const avg = P / N;
    const prizes: number[] = [];

    // Floor: 60% of the field at a nice ~0.4·avg denomination.
    const floorValue = niceFloor(0.4 * avg);
    const floorCount = Math.floor(N * 0.6);

    // Premium tiers grow triangularly (tier i holds i prizes) until they cover
    // the remaining ~40% of the field; the last tier absorbs any leftover count.
    const premiumCount = N - floorCount;
    let tierCount = 2;
    let tierMax = 3;
    while (tierMax < premiumCount) {
      tierCount++;
      tierMax += tierCount;
    }

    // Equal money per premium tier ⇒ tier i's value = tierMoney / i (tier 1 is
    // the single jackpot).
    const floorMoney = floorCount * floorValue;
    const premiumMoney = P - floorMoney;
    const premiumTierMoney = premiumMoney / tierCount;
    for (let tier = 1; tier <= tierCount; tier++) {
      const thisTierValue = premiumTierMoney / tier;
      let thisCount = tier;
      if (tier == tierCount) {
        thisCount = N - prizes.length - floorCount;
      }
      for (let i = 0; i < thisCount; i++) {
        prizes.push(thisTierValue);
      }
    }
    for (let i = 0; i < floorCount; i++) {
      prizes.push(floorValue);
    }

    // Reconcile the premium prizes: spread the leftover proportionally, then snap
    // each to a nice denomination — the jackpot to the NEAREST (up or down) so it
    // reads as a clean headline number, the lower tiers down.
    const remainingMoney = P - prizes.reduce((acc, num) => acc + num, 0);
    for (let i = 0; i < N - floorCount; i++) {
      const reconciled =
        prizes[i] +
        (prizes[i] / (premiumMoney - remainingMoney)) * remainingMoney;
      prizes[i] = i === 0 ? niceRound(reconciled) : niceFloor(reconciled);
    }

    // The floor settles the books: refill its balls with two adjacent nice
    // denominations so the whole set sums to exactly P — most at the floor value,
    // a few one notch up to absorb the remainder.
    const firstFloor = N - floorCount;
    const floorTarget =
      P - prizes.slice(0, firstFloor).reduce((acc, num) => acc + num, 0);
    const per = floorTarget / floorCount;
    const lo = Math.max(1, niceFloor(per));
    const hi = niceCeil(per);
    if (hi <= lo) {
      for (let i = 0; i < floorCount; i++) prizes[firstFloor + i] = lo;
      prizes[N - 1] += floorTarget - lo * floorCount;
    } else {
      let x = Math.round((floorTarget - lo * floorCount) / (hi - lo)); // count at hi
      x = Math.max(0, Math.min(floorCount, x));
      for (let i = 0; i < floorCount; i++) {
        prizes[firstFloor + i] = i < x ? hi : lo;
      }
      prizes[firstFloor] += floorTarget - (x * hi + (floorCount - x) * lo);
    }

    // High → low so the preview reads cleanly (the draw itself is random).
    prizes.sort((a, b) => b - a);
    return descale(prizes);
  }
  // Eliminated players whose mystery bounty hasn't been drawn yet (the "Later"
  // queue). Drives the bounty-draw launcher.
  get pendingBountyDraws(): any[] {
    const players = this.latestTourn?.players;
    if (!players || !this.isMysteryBounty) return [];
    return Object.keys(players)
      .filter((uid) => players[uid] && players[uid].bountyPending)
      .map((uid) => ({ uid, ...players[uid] }));
  }

  // Pending draws grouped by the player who made the knockouts (the "killer"),
  // since directors typically draw all of a player's bounties together. Each
  // group: { killerID, victims: pending-draw[] }.
  get pendingBountyDrawsByKiller(): any[] {
    const groups: { [killerID: string]: any } = {};
    for (const p of this.pendingBountyDraws) {
      const killerID = p.killerID || p.eliminationKillerID;
      if (!killerID) continue;
      if (!groups[killerID]) groups[killerID] = { killerID, victims: [] };
      groups[killerID].victims.push(p);
    }
    return Object.values(groups);
  }

  // Draw every pending bounty for one killer — ONE cloud call + ONE ceremony.
  drawAllForKiller(killerID: string): Promise<any> {
    const pairs = this.pendingBountyDraws
      .filter((p) => (p.killerID || p.eliminationKillerID) === killerID)
      .map((p) => ({
        playerID: p.uid,
        killerID: p.killerID || p.eliminationKillerID,
      }));
    return this.drawMysteryBountyBatch(pairs);
  }

  // Draw every pending bounty across all killers — ONE cloud call + ONE ceremony.
  drawAllPending(): Promise<any> {
    const pairs = this.pendingBountyDraws.map((p) => ({
      playerID: p.uid,
      killerID: p.killerID || p.eliminationKillerID,
    }));
    return this.drawMysteryBountyBatch(pairs);
  }
  // Undrawn mystery-bounty prize values, biggest first (the fullscreen-clock
  // strip). Once the pool is frozen this is the authoritative undrawn set;
  // before that it mirrors buildMysteryPrizePool's source choice (valid manual
  // list, else the generated distribution) so players see what will be drawn.
  get mysteryRemainingPrizes(): number[] {
    if (!this.isMysteryBounty) return [];
    const pool = this.latestTourn?.mysteryPool?.prizes;
    if (pool) {
      const arr: any[] = Array.isArray(pool) ? pool : Object.values(pool);
      return arr
        .filter((p: any) => p && !p.drawnBy)
        .map((p: any) => p.value || 0)
        .sort((a, b) => b - a);
    }
    const p = this.latestTourn?.params;
    if (!p) return [];
    const values =
      p.bountyPrizesManual &&
      Array.isArray(p.bountyPrizes) &&
      p.bountyPrizes.length === this.mysteryPrizeCount
        ? p.bountyPrizes
        : this.bountyPrizesPreview;
    return (values || []).slice().sort((a: number, b: number) => b - a);
  }
  // Live (read-only) preview of the prize distribution for the current pool +
  // estimated count, shown under the Payouts → Bounties sub-tab.
  get bountyPrizesPreview(): number[] {
    if (!this.isMysteryBounty) return [];
    const p = this.latestTourn?.params;
    if (!p) return [];
    return this.generateBountyPrizes(
      p.bountyPool || 0,
      this.mysteryPrizeCount,
      p.roundTo || 1,
    );
  }

  // --- Modular write handles (each op runs in the injection context, the same
  //     zone-stability requirement as the read streams). Keyed on the active
  //     tournament. (tablesAF/tableNamesAF/logAF were write-unused and dropped;
  //     log writes go through logEntry().) ---
  get tournamentAF() {
    const id = this.selectedTournId();
    return {
      update: (data: any) =>
        runInInjectionContext(this.injector, () =>
          update(ref(this.mdb, `tournaments/${id}`), data),
        ),
    };
  }
  get paramsAF() {
    const id = this.selectedTournId();
    return {
      update: (data: any) =>
        runInInjectionContext(this.injector, () =>
          update(ref(this.mdb, `tournaments/${id}/params`), data),
        ),
    };
  }
  get liveAF() {
    const id = this.selectedTournId();
    return {
      update: (data: any) =>
        runInInjectionContext(this.injector, () =>
          update(ref(this.mdb, `tournaments/${id}/live`), data),
        ),
    };
  }
  get levelsAF() {
    const id = this.selectedTournId();
    // Split parent: blind-structure edits fan out to the unfinished children.
    // Their structures were copied verbatim at split, so level keys match —
    // without the fan-out the lockstep clocks would derive different levels.
    const levelTargets = () => {
      const ids = [id];
      const splitInto = this.latestTourn?.splitInto;
      if (splitInto) {
        for (const cid in splitInto) {
          if (splitInto[cid]?.tournStatus !== "Finished") ids.push(cid);
        }
      }
      return ids;
    };
    return {
      update: (key: string, data: any) =>
        runInInjectionContext(this.injector, () =>
          Promise.all(
            levelTargets().map((tid) =>
              update(ref(this.mdb, `structures/${tid}/levels/${key}`), data),
            ),
          ),
        ),
      remove: (key: string) =>
        runInInjectionContext(this.injector, () =>
          Promise.all(
            levelTargets().map((tid) =>
              remove(ref(this.mdb, `structures/${tid}/levels/${key}`)),
            ),
          ),
        ),
      push: (data: any) =>
        runInInjectionContext(this.injector, () => {
          // Mint one key so parent and children share it (future per-key
          // edits then fan out to the same level everywhere).
          const newRef = push(ref(this.mdb, `structures/${id}/levels`));
          return Promise.all(
            levelTargets().map((tid) =>
              update(
                ref(this.mdb, `structures/${tid}/levels/${newRef.key}`),
                data,
              ),
            ),
          );
        }),
    };
  }
  get playersAF() {
    const id = this.selectedTournId();
    return {
      update: (key: string, data: any) =>
        runInInjectionContext(this.injector, () =>
          update(ref(this.mdb, `tournaments/${id}/players/${key}`), data),
        ),
    };
  }
  get payoutsAF() {
    const id = this.selectedTournId();
    return {
      update: (key: string, data: any) =>
        runInInjectionContext(this.injector, () =>
          update(ref(this.mdb, `payouts/${id}/${key}`), data),
        ),
      remove: (key: string) =>
        runInInjectionContext(this.injector, () =>
          remove(ref(this.mdb, `payouts/${id}/${key}`)),
        ),
    };
  }

  // --- Generic modular DB helpers (replace the old rootRef/baseRef/tournRef
  // compat refs). Root fan-out updates write at the db root with slash-keyed
  // paths; single-node ops take an explicit path. All wrapped in the injection
  // context (AngularFire zone-stability requirement). ---
  private rootUpdate(updates: any): Promise<void> {
    return runInInjectionContext(this.injector, () =>
      update(ref(this.mdb), updates),
    );
  }
  private nodeUpdate(path: string, data: any): Promise<void> {
    return runInInjectionContext(this.injector, () =>
      update(ref(this.mdb, path), data),
    );
  }
  private nodeSet(path: string, data: any): Promise<void> {
    return runInInjectionContext(this.injector, () =>
      set(ref(this.mdb, path), data),
    );
  }
  private nodeGet(path: string): Promise<any> {
    return runInInjectionContext(this.injector, () => get(ref(this.mdb, path)));
  }
  private nodePush(path: string, data?: any): any {
    return runInInjectionContext(this.injector, () =>
      data === undefined
        ? push(ref(this.mdb, path))
        : push(ref(this.mdb, path), data),
    );
  }
  private nodeTransaction(
    path: string,
    fn: (current: any) => any,
  ): Promise<any> {
    return runInInjectionContext(this.injector, () =>
      runTransaction(ref(this.mdb, path), fn),
    );
  }

  // Blind/break structure — a computed off the levels signal (NOT an
  // effect-pushed field): pulled consistently during CD so it can't change
  // mid-pass (NG0100) under zoneless. `get structure()` keeps every
  // `tournServ.structure`/`this.structure` read unchanged.
  private readonly _structure = computed(() =>
    this.buildStructure(this.levelsSig() || []),
  );
  get structure(): any[] {
    return this._structure();
  }

  // --- Plain mutable state (UI/scratch + clock; unchanged) ---
  // Per-table edit-mode flags, resized by the tables effect (mid-CD) → signal-
  // backed so the resize can't trip NG0100. ngModel-style element edits
  // (edits[key].edit = true) mutate the array in place; the resize reassigns.
  private _edits = signal<any[]>([]);
  get edits(): any[] {
    return this._edits();
  }
  set edits(v: any[]) {
    this._edits.set(v);
  }
  currentTab: string = "blinds";
  payoutsTab: number = 0;
  paramsUpdateValues: any;
  // Players enriched with their table name. Was an effect-pushed field (NG0100
  // under zoneless when set mid-CD); now a computed off the tournament +
  // tableNames signals — pulled consistently during CD. `get latestPlayers()`
  // keeps every read unchanged.
  private readonly _latestPlayers = computed(() => {
    const results = this.tournamentSig();
    const names = this.tableNamesSig();
    if (!results || !results.tournID) return null;
    // A tournament with zero registrations has no players node at all —
    // that's a loaded-but-empty tournament, not a loading one: return [].
    const arr = this.util.getObjArray(results.players || {});
    if (names && Object.keys(names).length) {
      arr.forEach((player: any) => {
        if (names[player.table - 1]) player.TableName = names[player.table - 1];
      });
    }
    return arr;
  });
  // null until the tournament + players have loaded — callers must guard.
  // (An earlier `!` here made strict templates "prove" non-null and NG8107
  // then suggested deleting real ?. guards → prod crash. Keep this honest.)
  get latestPlayers(): any[] | null {
    return this._latestPlayers();
  }
  possibleBlinds!: any[] | null;
  allPossibleBlinds!: any[] | null;
  levelsPath!: string;
  paramsPath!: string;
  chipDistThresh: number = 6;
  mainTab: string = "blinds";
  // bv-tabs two-way binds [(selected)]="mainIndex"; it's also set from async
  // callbacks (drawSeats .then → 3, the seat-draw dialog's afterClosed → 0),
  // which race the dialog CD and trip NG0100 on the two-way binding. Signal-
  // backed so those writes re-loop CD cleanly under zoneless.
  // Set by splitTournament(); consumed by the constructor effect that opens
  // the seating tab once the split parent's children are loaded.
  private openSeatingWhenSplitLoads: string | null = null;
  private _mainIndex = signal(0);
  get mainIndex(): number {
    return this._mainIndex();
  }
  set mainIndex(v: number) {
    this._mainIndex.set(v);
  }
  blindsTab: number = 0;
  playersTab: number = 0;
  tournamentLogID!: string;
  // The clock is a pure `computed()` derived from latestLive + the blind
  // structure + a 500ms tick counter + the server-clock delta — no imperative
  // mutation, no manual version bump. All side-effects (sounds, tracking, the
  // real() flag, the end-of-structure auto-stop) live in the clock effect set up
  // in the constructor, NOT in the math. `get clock()` keeps every
  // `tournServ.clock.X` template read unchanged. Before a tournament+structure
  // load it returns a stable empty object (matching the old `_clock = {}` so all
  // un-chained `clock.X` reads still resolve to undefined, not a null deref).
  private readonly emptyClock: any = {};
  private readonly tickCounter = signal(0);
  private readonly deltaSig = signal(0);
  private readonly clockSig = computed(() => this.computeClock());
  get clock(): any {
    // Independent-clocks split parent: the hub has no clock of its own — the
    // accessor serves the currently ROTATING child's clock instead, so every
    // clock-face component (timer, running time, next break, blinds, ante)
    // flips together as the rotation advances. Internal effects (level-change
    // sound, overrun auto-stop) read clockSig directly and stay on the
    // parent's own frozen live.
    if (this.isIndependentSplitParent) {
      const child = this.splitRotationChild;
      if (child) {
        const players: any[] = Object.values(child.players || {});
        const remaining =
          players.length - players.filter((p: any) => p.finish).length;
        return this.computeClockFor(child.live, remaining);
      }
    }
    return this.clockSig();
  }
  slider: any = {};
  sliderLevel: any = {};
  // Manual-payout editable copy. Set wholesale by setLocalPayouts() from the
  // payouts effect (which runs mid-CD) → signal-backed so the effect's write is
  // re-looped cleanly instead of tripping NG0100. ngModel still edits elements
  // in place ([i].award/.percent) on the same array.
  private _localPayouts = signal<any>(undefined);
  get localPayouts(): any[] {
    return this._localPayouts();
  }
  set localPayouts(v: any[]) {
    this._localPayouts.set(v);
  }
  // Manual-payout totals deltas — set by updateAwardDeltas(), which the payouts
  // effect calls (setLocalPayouts/syncLocalPayoutsFromLatest) → signal-backed to
  // avoid NG0100 under zoneless (same as localPayouts).
  private _awardDelta = signal<number | undefined>(undefined);
  get awardDelta(): number {
    return this._awardDelta()!;
  }
  set awardDelta(v: number) {
    this._awardDelta.set(v);
  }
  private _percentDelta = signal<number | undefined>(undefined);
  get percentDelta(): number {
    return this._percentDelta()!;
  }
  set percentDelta(v: number) {
    this._percentDelta.set(v);
  }
  // Manual mystery-bounty prize editing (Payouts → Bounties sub-tab). Same
  // signal-backed local-copy + delta shape as the payout editor above.
  private _localBountyPrizes = signal<any>(undefined);
  get localBountyPrizes(): any[] {
    return this._localBountyPrizes();
  }
  set localBountyPrizes(v: any[]) {
    this._localBountyPrizes.set(v);
  }
  private _bountyDelta = signal<number | undefined>(undefined);
  get bountyDelta(): number {
    return this._bountyDelta()!;
  }
  set bountyDelta(v: number) {
    this._bountyDelta.set(v);
  }
  needPayoutsInit!: boolean;
  playerMoves: any[] = [];
  tableBreaking: any;
  settingsOpen: boolean = false;
  private _blindTabVisit = signal(false);
  get blindTabVisit() {
    return this._blindTabVisit();
  }
  set blindTabVisit(v: boolean) {
    this._blindTabVisit.set(v);
  }
  leaguesToAdd: any;
  // Loading flags are set true in click handlers and cleared in async .then()
  // callbacks; the clear won't trigger CD under zoneless, so they're
  // signal-backed (get/set keeps every template read + internal assignment
  // unchanged). See [[feedback_rearch_fix_antipatterns]] pattern.
  private _loadingRegister = signal(false);
  get loadingRegister() {
    return this._loadingRegister();
  }
  set loadingRegister(v: boolean) {
    this._loadingRegister.set(v);
  }
  private _loadingMovePlayer = signal(false);
  get loadingMovePlayer() {
    return this._loadingMovePlayer();
  }
  set loadingMovePlayer(v: boolean) {
    this._loadingMovePlayer.set(v);
  }
  private _loadingUnRegisterPlayer = signal(false);
  get loadingUnRegisterPlayer() {
    return this._loadingUnRegisterPlayer();
  }
  set loadingUnRegisterPlayer(v: boolean) {
    this._loadingUnRegisterPlayer.set(v);
  }
  private _loadingEliminationPlayer = signal(false);
  get loadingEliminationPlayer() {
    return this._loadingEliminationPlayer();
  }
  set loadingEliminationPlayer(v: boolean) {
    this._loadingEliminationPlayer.set(v);
  }
  private _loadingAddTable = signal(false);
  get loadingAddTable() {
    return this._loadingAddTable();
  }
  set loadingAddTable(v: boolean) {
    this._loadingAddTable.set(v);
  }
  private _loadingDrawSeats = signal(false);
  get loadingDrawSeats() {
    return this._loadingDrawSeats();
  }
  set loadingDrawSeats(v: boolean) {
    this._loadingDrawSeats.set(v);
  }
  private _loadingBountyDraw = signal(false);
  get loadingBountyDraw() {
    return this._loadingBountyDraw();
  }
  set loadingBountyDraw(v: boolean) {
    this._loadingBountyDraw.set(v);
  }
  private _loadingUndo = signal(false);
  get loadingUndo() {
    return this._loadingUndo();
  }
  set loadingUndo(v: boolean) {
    this._loadingUndo.set(v);
  }
  private _loadingPlayerMoves = signal(false);
  get loadingPlayerMoves() {
    return this._loadingPlayerMoves();
  }
  set loadingPlayerMoves(v: boolean) {
    this._loadingPlayerMoves.set(v);
  }
  private _loadingChipOp = signal(false);
  get loadingChipOp() {
    return this._loadingChipOp();
  }
  set loadingChipOp(v: boolean) {
    this._loadingChipOp.set(v);
  }
  lastActionIsRegister: boolean = false;
  // Edge-trigger guards for the seating notifications (break / balance / final
  // table) — pop once per need-episode, not on every tournament emit (e.g. a
  // bounty draw writes to the tournament and would otherwise re-pop the toast).
  private notifiedBreak = false;
  private notifiedBalance = false;
  private notifiedFinalTable = false;
  private notifiedShootoutAdvance = false;
  // Fake-fullscreen fallback for browsers without the Fullscreen API (iOS
  // Chrome): hides the tournament header + tab strip so the clock gets the
  // space — the browser frame itself stays. Per-screen, in-memory only.
  readonly fakeFullscreen = signal(false);
  private previousHandForHand: boolean = false;
  // Tournaments whose one-time legacy param backfill/migration has already run
  // this session (replaces re-running the auto-init writes on every emit).
  private bootstrappedTournIds = new Set<string>();

  constructor(
    private util: UtilService,
    private tracking: TrackingService,
    private auth: AuthService,
    private profServ: ProfileService,
    private clubServ: ClubService,
    private translate: TranslateService,
    private snackBar: BvToastService,
    private audio: AudioService,
    private leagueServ: LeagueService,
    private dialog: BvDialogService,
    private routing: RoutingService,
    private api: ApiService,
    private clockOverlay: ClockOverlayService,
  ) {
    // --- Reactive cascade (was the per-tournament subscribes in init()) ---

    // Each effect tracks ONLY its primary signal and runs its body untracked,
    // so it fires exactly when the old `.subscribe` did (on that node's emit) —
    // without the transitive signal reads inside (latestTables/latestPayouts/
    // club/profile via the calc cascade) silently widening the dependency set
    // and causing extra cascade runs or duplicate balance dialogs.

    // Main tournament effect. Per-emit: enrich players + run the clock. Then a
    // ONCE-per-tournID legacy backfill (the old per-emit auto-init writes) —
    // these default missing params on tournaments created before those params
    // existed, so they only need to run on first load, not every emit.
    effect(() => {
      const results = this.tournamentSig();
      untracked(() => {
        if (!results || !results.tournID) return;

        // --- per emit (read-derived UI) ---
        // (latestPlayers is now a computed; the clock is now a computed too — it
        // re-derives from latestLive automatically, so no setClock()/tick() here.
        // Clock side-effects are handled by the clock effect below.)
        if (results.params && (results.params.seated || results.live?.seated))
          this.checkBalance();
        this.util.loading = false;

        // --- once per tournID (legacy param backfill + migrations) ---
        // reads-trigger-writes, but bounded to first load. TODO: move this to a
        // server-side backfill so the client write can be dropped entirely.
        if (this.bootstrappedTournIds.has(results.tournID)) return;
        this.bootstrappedTournIds.add(results.tournID);
        if (results.params && results.params.tournInit)
          this.changeParam("tournInit", null);
        if (!results.params.manageRegistrations && !results.params.actPlayers) {
          this.changeParam("actPlayers", results.params.expPlayers);
        }
        if (!results.params.registrations)
          this.changeParam("registrations", "open");
        if (!results.params.tournamentType)
          if (results.params.rebuyTournament)
            this.changeParam("tournamentType", "rebuy_tournament");
          else this.changeParam("tournamentType", "reentry_tournament");
        if (
          results.params.tournamentType == "reentry_tournament" &&
          !results.params.reentries &&
          results.params.reentries != 0
        )
          this.changeParam("reentries", 0);
        if (
          results.params.tournamentType == "reentry_tournament" &&
          !results.params.reentriesManual &&
          results.params.reentriesManual != false
        )
          this.changeParam("reentriesManual", false);
        if (!results.params.reentryChips && results.params.startingStack) {
          this.changeParam("reentryChips", results.params.startingStack);
          this.changeParam("reentryChipsManual", false);
        }
        if (!results.params.reentryCost && results.params.buyin) {
          this.changeParam("reentryCost", results.params.buyin);
        }
        this.rehydrateChipColors(results);
        this.checkForGhosts();
      });
    });

    // (Levels → blind/break structure is now the `structure` computed.)

    // Clock side-effects. The clock itself is a pure computed; this effect reads
    // it each tick and fires the transition-driven side-effects by edge-detecting
    // against the previous snapshot — replacing the minutePlayed/secondPlayed gate
    // fields and the writebacks (latestLive.seconds / stopTimer) that used to live
    // inside setClock(). All non-pure work that used to be interleaved with the
    // clock math lives here.
    let prevClock: any = null;
    effect(() => {
      const c = this.clockSig();
      untracked(() => {
        if (!c || c.seconds == null) {
          prevClock = null;
          return;
        }
        const live = this.latestLive;
        const running = !!live?.running;
        const h4h = !!live?.hand_for_hand;

        // Level advanced by exactly one while running / hand-for-hand → change
        // sound + tracking + (from level 2) the real-tournament flag.
        if (prevClock && c.level === prevClock.level + 1 && (running || h4h)) {
          this.audio.play("change");
          this.tracking.track("Tick Next Level", {
            from: prevClock.level,
            to: c.level,
            seconds: c.seconds,
          });
          if (
            c.level >= 2 &&
            !this.latestTourn?.params?.real &&
            this.profServ.latestProfile?.locationData?.traits?.ip_address
          ) {
            this.real(this.profServ.latestProfile.locationData);
            this.tracking.track("Log Real Tournament", this.latestTourn.params);
          }
        }

        // Minute warning: rising edge at 60s remaining (skip 1-minute levels).
        if (
          !c.atEnd &&
          c.timeSec === 60 &&
          c.levelTime !== 1 &&
          (!prevClock || prevClock.timeSec !== 60)
        )
          this.audio.play("minute");

        // Final-5-seconds countdown: once per new second.
        if (
          !c.atEnd &&
          c.timeSec < 6 &&
          c.timeSec > 0 &&
          (!prevClock || prevClock.timeSec !== c.timeSec)
        )
          this.audio.play("second");

        // End-of-structure overrun: persist the auto-stop once (was the inline
        // latestLive.seconds writeback + stopTimer in setClock()).
        if (
          c.overrun &&
          running &&
          // Synchronized split children mirror the parent's clock — a child
          // stopping itself would drift out of lockstep (the parent stops for
          // all). Independent children behave like normal tournaments.
          !this.isSyncedSplitChild &&
          (!prevClock || !prevClock.overrun)
        )
          this.stopTimer(c.seconds);

        prevClock = c;
      });
    });

    // Payouts (was payoutsSub).
    effect(() => {
      const results = this.payoutsSig();
      untracked(() => {
        if (results) {
          if (this.needPayoutsInit) {
            this.setLocalPayouts();
            this.needPayoutsInit = false;
          } else if (
            this.latestTourn &&
            this.latestTourn.params &&
            this.latestTourn.params.payoutManual
          ) {
            // Reflect server-driven payout changes (rebuy/addon/register
            // recalc) in the manual editable view without clobbering edit focus.
            this.syncLocalPayoutsFromLatest();
          }
          // Pre-start stack recompute only (a payout change usually means a
          // rebuy/param recalc that also changed chip amounts). Player payout
          // assignment no longer happens here — the server onPayoutTableChange
          // trigger reconciles players/{uid}/payout whenever /payouts changes,
          // so N connected devices no longer race to write it.
          if (this.latestPlayers && this.latestPlayers.length > 0) {
            this.updateStacks();
          }
        }
      });
    });

    // Tables → keep exactly one edit-flag per table (was tablesSub, which
    // pushed cumulatively on every emit → edits grew unbounded). Grow/shrink to
    // match without resetting flags the user has toggled. Auto-clears to [] when
    // selectedTournId→null shrinks tablesSig to [].
    effect(() => {
      const results = this.tablesSig();
      untracked(() => {
        if (results) {
          // Reassign (not in-place mutate) so the signal notifies; preserve
          // existing edit flags where the table still exists.
          const cur = this.edits || [];
          if (cur.length !== results.length) {
            this.edits = results.map(
              (_: any, i: number) => cur[i] || { edit: false },
            );
          }
        }
      });
    });

    // After a split, open the hub's seating tab once the seated children have
    // loaded (the tab only exists then; selectTournament resets mainIndex to
    // 0 on the way in, so this has to wait for the data).
    effect(() => {
      const target = this.openSeatingWhenSplitLoads;
      const t = this.tournamentSig();
      const ready = this.hasSplitSeating;
      untracked(() => {
        if (target && t?.tournID === target && ready) {
          this.openSeatingWhenSplitLoads = null;
          this.mainIndex = 3; // clock, blinds, players, seating
          // Same closing step as a seat draw: the server put the seats
          // overlay on every fullscreen clock of the parent; this dialog on
          // the initiating device is what takes it down.
          this.translate
            .get(["SEAT_DRAW_COMPLETE", "DISMISS"])
            .subscribe((res: any) => {
              this.dialog
                .open(AlertDialogComponent, {
                  data: {
                    message: res["SEAT_DRAW_COMPLETE"] || "Seat draw complete",
                    closeButton: res["DISMISS"] || "Dismiss",
                  },
                  panelClass: "alert-dialog-container",
                })
                .afterClosed()
                .subscribe(() => this.clockOverlay.dismiss());
            });
        }
      });
    });

    // Cross-device hand-for-hand toast (was liveSub), reading the tournament's
    // live child; edge-detected so it fires only on an actual state flip.
    effect(() => {
      const liveData = this.tournamentSig()?.live;
      untracked(() => {
        if (liveData && typeof liveData.hand_for_hand === "boolean") {
          const currentHandForHand = liveData.hand_for_hand;
          if (this.previousHandForHand !== currentHandForHand) {
            if (currentHandForHand) {
              this.snackBar.open(
                this.translate.instant("HAND_FOR_HAND_STARTED"),
                "",
                { duration: 3000 },
              );
            } else {
              this.snackBar.open(
                this.translate.instant("HAND_FOR_HAND_ENDED"),
                "",
                { duration: 3000 },
              );
            }
          }
          this.previousHandForHand = currentHandForHand;
        }
      });
    });
  }
  async checkOldChipset() {
    const snapshot = await this.nodeGet(
      "tournaments/" + this.tournID + "/params/chipset",
    );
    if (snapshot.exists()) {
      let chipset = snapshot.val();

      // if string convert to array
      if (typeof chipset == "string") {
        // remove trailing comma if exists
        while (chipset.substr(-1) == ",") chipset = chipset.slice(0, -1);

        if (this.latestTourn?.params) {
          this.latestTourn.params.chipset = chipset
            .split(",")
            .map((chip: string) => parseFloat(chip));
        }
        const converted = chipset
          .split(",")
          .map((chip: string) => parseFloat(chip));
        await this.nodeSet(
          "tournaments/" + this.tournID + "/params/chipset",
          converted,
        );
        this.clubServ.updateTournParams(
          this.tournID,
          { chipset: converted },
          this.latestTourn?.clubID,
        );
      }
    }
  }
  updateLastLogin() {
    if (this.auth.uid) {
      this.nodeSet(
        "profiles/" + this.auth.uid + "/lastLogin",
        serverTimestamp(),
      );
    }
  }

  async init(tournID: string) {
    this.needPayoutsInit = true;
    this.mainTab = "clock";
    // Re-key all read signals onto this tournament (the cascade effects in the
    // constructor react automatically). The old per-init stream/subscribe wiring
    // is gone; only the genuine one-time setup remains here.
    this.selectedTournId.set(tournID);
    this.updateLastLogin();
    await this.checkOldChipset();
    const tournPath = "tournaments/" + tournID;
    this.levelsPath = "structures/" + tournID + "/levels";
    this.paramsPath = tournPath + "/params";
    // Write handles are now modular getters keyed on selectedTournId (above);
    // no per-init handle construction needed.
    this.initClock();
  }
  initLogs() {
    // Flip on the lazy log listener (logSig keys on logEnabled).
    this.logEnabled.set(true);
    this.tournamentLogID = this.tournID;
  }
  unsubscribeLogs() {
    this.logEnabled.set(false);
  }
  clearTournament() {
    // Tearing down the active tournament: null the id → every read signal
    // resets to null/[] via its switchMap. Only UI/scratch + clock state is
    // reset here now.
    this.selectedTournId.set(null);
    this.logEnabled.set(false);
    this.currentTab = "blinds";
    // structure + latestPlayers are computeds off their signals → reset
    // automatically when the id clears.
    this.paramsUpdateValues = null;
    this.possibleBlinds = null;
    this.allPossibleBlinds = null;
    this.chipDistThresh = 6;
    this.mainTab = "blinds";
    this.blindsTab = 0;
    this.playersTab = 0;
    this.sliderLevel = null;
    this.mainIndex = 0;
    // The clock is a computed off latestLive/structure — it returns null once
    // selectedTournId is cleared, so no manual reset needed.
    // Reset hand-for-hand state tracking
    this.previousHandForHand = false;
  }
  createTournament(
    tournName: string,
    tournDate: any,
    tournTime: any,
    params: any,
    template?: any,
    location?: any,
  ) {
    this.util.loading = true;
    if (this.clubServ.latestClub) {
      if (template && template.params) {
        params = template.params;
        params.prizepool = 0;
        if (!params.tournamentType)
          if (params.rebuyTournament)
            params.tournamentType = "rebuy_tournament";
          else params.tournamentType = "freezeout";
      } else {
        params.addedPrize = 0;
        params.extraSeats = 0;
        // Respect a perTable chosen in the create dialog (shootout card) —
        // coerced: the number input hands over a string, and stored strings
        // break strict heads-up (=== 2) checks downstream.
        params.perTable = Number(params.perTable) || 9;
        params.numberPlayersFinalTable = params.perTable;
        params.tournInit = true;
        params.calcPlacesPaidManual = false;
        params.payoutManual = false;
        params.payoutManualRounding = true;
        if (params.buyin) {
          params.rebuyCost = params.buyin;
          params.addonCost = params.buyin;
          params.reentryCost = params.buyin;
        }
      }
      const playLimit: number | null = this.clubServ.getPlayerLimit();
      if (!params.manageRegistrations) {
        if (playLimit && params.expPlayers > playLimit)
          params.actPlayers = playLimit;
        else params.actPlayers = params.expPlayers;
      } else {
        if (playLimit) {
          params.maxPlayers = this.clubServ.getPlayerLimit();
        } else {
          params.maxPlayers = 9999;
        }
      }
      if (!params.reentryChips && params.startingStack)
        params.reentryChips = params.startingStack;
      if (!params.reentryCost && params.buyin)
        params.reentryCost = params.buyin;

      const tournTimestamp = localDateTimeToTs(tournDate, tournTime);
      var newTournKey = this.nodePush("tournaments").key;
      let updates: any = {};
      let leagues: any;
      if (this.leaguesToAdd) {
        leagues = this.util.copyObject(this.leaguesToAdd);
        this.leaguesToAdd = null;
      }
      updates["tournaments/" + newTournKey] = {
        clubID: this.clubServ.clubID,
        tournID: newTournKey,
        tournName: tournName,
        tournTimestamp: tournTimestamp,
        tournStatus: "Scheduled",
        params: params,
        owner: this.clubServ.latestClub.owner,
        playerCount: 0,
        registrations: "open",
        live: {
          index: 0,
          seconds: 0,
          running: false,
          started: false,
          out: 0,
          rebuys: 0,
          addons: 0,
          undoIdx: 0,
          reentries: 0,
          hand_for_hand: false,
        },
      };
      updates["clubs/" + this.clubServ.clubID + "/tournaments/" + newTournKey] =
        {
          tournID: newTournKey,
          tournName: tournName,
          tournTimestamp: tournTimestamp,
          tournStatus: "Scheduled",
          sort: -tournTimestamp,
          params: params,
        };
      if (leagues) {
        updates["tournaments/" + newTournKey].leagues = leagues;
        updates[
          "clubs/" + this.clubServ.clubID + "/tournaments/" + newTournKey
        ].leagues = leagues;
        for (let l in leagues)
          updates["leagues/" + l + "/tournaments/" + newTournKey] = {
            tournID: newTournKey,
            tournName: tournName,
          };
      }
      // Venue snapshot ({locationID, name, city}); the club mirror copy is
      // what the lobby card renders.
      if (location) {
        updates["tournaments/" + newTournKey].location = location;
        updates[
          "clubs/" + this.clubServ.clubID + "/tournaments/" + newTournKey
        ].location = location;
      }
      if (template && template.structure)
        updates["structures/" + newTournKey + "/levels"] = template.structure;

      updates["profiles/" + this.profServ.uid + "/defaults/anteType"] =
        params.anteType || "big_blind_ante";
      updates["profiles/" + this.profServ.uid + "/defaults/chipset"] =
        params.chipset;
      updates["profiles/" + this.profServ.uid + "/defaults/duration"] =
        params.duration;
      updates["profiles/" + this.profServ.uid + "/defaults/expPlayers"] =
        params.expPlayers;
      updates["profiles/" + this.profServ.uid + "/defaults/initSmallBlind"] =
        params.initSmallBlind;
      updates["profiles/" + this.profServ.uid + "/defaults/managePayouts"] =
        params.managePayouts;
      updates[
        "profiles/" + this.profServ.uid + "/defaults/manageRegistrations"
      ] = params.manageRegistrations;
      updates["profiles/" + this.profServ.uid + "/defaults/tournamentType"] =
        params.tournamentType;
      updates["profiles/" + this.profServ.uid + "/defaults/rebuyChips"] =
        params.rebuyChips;
      updates["profiles/" + this.profServ.uid + "/defaults/rebuyChips"] =
        params.reentryChips;
      updates["profiles/" + this.profServ.uid + "/defaults/addonChips"] =
        params.addonChips;
      updates["profiles/" + this.profServ.uid + "/defaults/startingStack"] =
        params.startingStack;
      if (params.rebuys)
        updates["profiles/" + this.profServ.uid + "/defaults/rebuyRatio"] =
          params.rebuys / params.expPlayers;
      if (params.addons)
        updates["profiles/" + this.profServ.uid + "/defaults/addonRatio"] =
          params.addons / params.expPlayers;
      if (params.addonChips && params.startingStack)
        updates["profiles/" + this.profServ.uid + "/defaults/addonChipsRatio"] =
          params.addonChips / params.startingStack;
      params.reentryRatio =
        params.reentries && params.expPlayers
          ? params.reentries / params.expPlayers
          : 0.333;
      if (params.buyin) {
        updates["profiles/" + this.profServ.uid + "/defaults/buyin"] =
          params.buyin;
        updates["profiles/" + this.profServ.uid + "/defaults/rebuyCost"] =
          params.buyin;
        updates["profiles/" + this.profServ.uid + "/defaults/addonCost"] =
          params.buyin;
        updates["profiles/" + this.profServ.uid + "/defaults/reentryCost"] =
          params.buyin;
      }

      this.rootUpdate(updates)
        .then(() => {
          this.util.loading = false;
          this.logEntry("CREATE_TOURNAMENT", undefined, newTournKey);
          this.routing.toTournament(newTournKey);
          this.api.sendEvent(
            "EVT_TOURNAMENT_CREATED",
            this.profServ.latestProfile,
          );
        })
        .catch((error: any) => {
          // Surface the real failure instead of letting it bubble to the
          // generic global handler, which hides the cause and leaves the
          // loading overlay stuck on. The console.error carries the actual
          // Firebase error (permission denied, invalid value, etc.).
          this.util.loading = false;
          console.error("Error creating tournament:", error);
          this.snackBar.open(
            this.translate.instant("GENERIC_ERROR"),
            this.translate.instant("OK"),
            { duration: 5000 },
          );
        });
    } else {
      // No active club: the write was never attempted. Clear the overlay and
      // tell the user instead of hanging silently on the loading state.
      this.util.loading = false;
      console.error(
        "createTournament aborted: no active club (clubServ.latestClub is null)",
      );
      this.snackBar.open(
        this.translate.instant("GENERIC_ERROR"),
        this.translate.instant("OK"),
        { duration: 5000 },
      );
    }
  }
  /**
   * Create one or more new tournaments by duplicating a source tournament.
   * Mirrors `createTournament()`'s multi-path-update pattern, including the
   * leagues cascade and `tournStatus` field naming.
   *
   * The caller is responsible for fetching the source (tourn / levels /
   * payouts). The in-tournament REPLAY button passes `latestTourn`,
   * `latestLevels`, `latestPayouts` directly. The lobby duplicate dialog
   * uses `loadTournamentForDuplicate(srcID)` to fetch on demand.
   *
   * @param source - {tourn, levels, payouts}
   * @param occurrences - one entry per tournament to materialize
   * @param opts.carryPlayers - copy registered players (with stats reset).
   *                            Only meaningful when source has
   *                            manageRegistrations and a player list.
   * @returns array of new tournIDs in the same order as `occurrences`
   */
  duplicateTournament(
    source: { tourn: any; levels: any; payouts: any[] | null },
    occurrences: Array<{ tournTimestamp: number; tournName: string }>,
    opts: { carryPlayers: boolean } = { carryPlayers: false },
  ): Promise<string[]> {
    const baseParams = this.util.copyObject(source.tourn.params);
    baseParams.addedPrize = 0;
    baseParams.extraSeats = 0;
    baseParams.undoIdx = 0;
    baseParams.tournInit = true;
    if (baseParams.prizepool) baseParams.prizepool = 0;

    const sourceLeagues = source.tourn.leagues;
    const updates: any = {};
    // Payouts are written in a SECOND pass after the main update resolves.
    // The `payouts/$tournId` security rule has no escape hatch for "newly-
    // created" — it looks up `root.child('tournaments').child($tournId).
    // child('clubID')` to verify ownership, and in a multi-path update `root`
    // sees the pre-update state (where the new tournament doesn't exist yet),
    // so the write would be denied. `createTournament()` follows the same
    // pattern — it never writes payouts in its multi-path update.
    const payoutUpdates: any = {};
    const newTournIDs: string[] = [];

    for (const occ of occurrences) {
      const newKey = this.nodePush("tournaments").key;
      newTournIDs.push(newKey);

      const params = this.util.copyObject(baseParams);

      const tObj: any = {
        clubID: source.tourn.clubID,
        tournID: newKey,
        tournName: occ.tournName,
        tournTimestamp: occ.tournTimestamp,
        tournStatus: "Scheduled",
        params,
        owner: source.tourn.owner,
        playerCount: 0,
        registrations: "open",
        live: {
          index: 0,
          seconds: 0,
          running: false,
          started: false,
          out: 0,
          rebuys: 0,
          addons: 0,
          undoIdx: 0,
          reentries: 0,
          hand_for_hand: false,
        },
      };

      const clubTourn: any = {
        params,
        sort: -occ.tournTimestamp,
        tournID: newKey,
        tournName: occ.tournName,
        tournStatus: "Scheduled",
        tournTimestamp: occ.tournTimestamp,
      };

      // Player carry-forward (single-duplicate "run it back").
      if (
        opts.carryPlayers &&
        source.tourn.params.manageRegistrations &&
        source.tourn.players
      ) {
        const players = this.util.copyObject(source.tourn.players);
        const dupBountyMode =
          params.bountyMode || (params.bountyTournament ? "fixed" : "none");
        for (const p in players) {
          players[p].addon = 0;
          players[p].rebuys = 0;
          players[p].kos = 0;
          players[p].regStatus = "Registered";
          players[p].finish = null;
          players[p].stack = source.tourn.params.startingStack;
          players[p].seatSort = null;
          players[p].payout = null;
          players[p].killer = null;
          players[p].killerID = null;
          players[p].points = null;
          players[p].leagueFinish = null;
          // Reset ALL prior-run state — none of it may carry into a fresh
          // duplicate (bug: duplicated players showed the source tournament's
          // bounty winnings). Bounty: drop collected winnings + the mystery
          // pending-draw marker; reset the head value to the per-player amount
          // for fixed (regPlayer would set this; the duplicate writes players
          // directly) and clear it for non-fixed modes.
          players[p].bountyWinnings = null;
          players[p].bountyPending = null;
          players[p].bountyValue =
            dupBountyMode === "fixed" ? params.bountyPerPlayer || 0 : null;
          // Elimination metadata (mirror the server ELIMINATION-undo clear).
          players[p].reentryPossible = null;
          players[p].eliminatedAt = null;
          players[p].eliminatedAtSeconds = null;
          players[p].eliminatedAtTimestamp = null;
          players[p].eliminationPosition = null;
          players[p].eliminationKillerName = null;
          players[p].eliminationKillerID = null;
          players[p].locked = null;
        }
        tObj.players = players;
        tObj.playerCount = source.tourn.playerCount;
        clubTourn.playerCount = source.tourn.playerCount;
      }

      // Venue carries over with the rest of the setup.
      if (source.tourn.location) {
        tObj.location = source.tourn.location;
        clubTourn.location = source.tourn.location;
      }

      // Leagues cascade — mirror createTournament().
      if (sourceLeagues) {
        tObj.leagues = sourceLeagues;
        clubTourn.leagues = sourceLeagues;
        for (const lID in sourceLeagues) {
          updates[`leagues/${lID}/tournaments/${newKey}`] = {
            tournID: newKey,
            tournName: occ.tournName,
          };
        }
      }

      updates[`tournaments/${newKey}`] = tObj;
      updates[`clubs/${source.tourn.clubID}/tournaments/${newKey}`] = clubTourn;

      if (source.levels) {
        updates[`structures/${newKey}/levels`] = source.levels;
      }
      if (source.tourn.params.managePayouts && source.payouts) {
        payoutUpdates[`payouts/${newKey}`] = source.payouts;
      }
    }

    return this.rootUpdate(updates).then(() => {
      // Second pass: payouts only. Now that the tournaments exist, the
      // payouts rule's `root.child('tournaments').child($tournId).child(
      // 'clubID')` lookup resolves and the owner/staff check passes.
      if (Object.keys(payoutUpdates).length === 0) return newTournIDs;
      return this.rootUpdate(payoutUpdates).then(() => newTournIDs);
    });
  }

  /**
   * Read a tournament's full duplicate-source bundle (tournament object,
   * blind levels, payouts) in a single round trip. Used by the lobby
   * duplicate/repeat flows where `latestTourn` isn't populated.
   */
  async loadTournamentForDuplicate(
    srcID: string,
  ): Promise<{ tourn: any; levels: any; payouts: any[] | null }> {
    const [tournSnap, levelsSnap, payoutsSnap] = await Promise.all([
      this.nodeGet("tournaments/" + srcID),
      this.nodeGet("structures/" + srcID + "/levels"),
      this.nodeGet("payouts/" + srcID),
    ]);
    return {
      tourn: tournSnap.val(),
      levels: levelsSnap.val(),
      payouts: payoutsSnap.val(),
    };
  }

  /**
   * Merge multiple tournaments into a new tournament (for flights/Day 2 scenarios)
   * Calls the server-side Cloud Function to perform the merge.
   * @param name - Name for the merged tournament
   * @param tournamentIds - Array of tournament IDs to merge
   * @param timestamp - Optional timestamp for the merged tournament
   */
  async mergeTournaments(
    name: string,
    tournamentIds: string[],
    timestamp?: number,
  ): Promise<void> {
    if (tournamentIds.length < 2) {
      throw new Error("At least 2 tournaments are required for merging");
    }

    this.util.loading = true;

    try {
      const result = await this.api.mergeTournaments(
        name,
        tournamentIds,
        timestamp,
      );

      if (result.success && result.tournID) {
        this.routing.toTournament(result.tournID);
      }

      this.util.loading = false;
    } catch (error) {
      this.util.loading = false;
      throw error;
    }
  }

  /**
   * Split a Scheduled tournament's registered players into N child
   * tournaments of at most maxPlayers each (server-side, atomic). Navigates
   * to the parent, which becomes the control hub.
   */
  async splitTournament(
    tournID: string,
    maxPlayers: number,
    clockMode: string = "synchronized",
  ): Promise<void> {
    this.util.loading = true;
    try {
      const result = await this.api.splitTournament(
        tournID,
        maxPlayers,
        clockMode,
      );
      if (result.success) {
        // The split seats every child — land on the hub's seating tab (the
        // effect below opens it once the children have loaded).
        this.openSeatingWhenSplitLoads = tournID;
        this.routing.toTournament(tournID);
      }
      this.util.loading = false;
    } catch (error) {
      this.util.loading = false;
      throw error;
    }
  }

  // Resolve which tournament a player op should hit: explicit target first,
  // else (on a Split parent) the child the player belongs to, else the
  // current tournament. Lets the parent's players tab route KO/rebuy/addon/
  // unregister/stack ops into the right child.
  private opTournID(playerID?: string, targetTournID?: string): string {
    if (targetTournID) return targetTournID;
    if (playerID && this.isSplitParent) {
      const row = (this.splitPlayers || []).find(
        (p: any) => p.uid === playerID,
      );
      if (row?._childTournID) return row._childTournID;
    }
    return this.tournID;
  }

  goTab(tab: string) {
    this.currentTab = tab;
  }
  // Pure builder: returns the ordered blind/break structure. Backs the
  // `structure` computed (below) — must NOT mutate service state, or it
  // re-introduces the ExpressionChanged/effect-mid-CD problem under zoneless.
  buildStructure(results: any) {
    const structure: any[] = [];
    let blinds: any[] = [];
    let breaks: any[] = [];
    let breakLevels = [];
    for (let level of results) {
      if (level.sb) blinds.push(level);
      else {
        if (
          level.afterLevel >= 0 &&
          breakLevels.indexOf(level.afterLevel) == -1
        ) {
          breaks.push(level);
          breakLevels.push(level.afterLevel);
        }
      }
    }

    blinds.sort(this.blindsSort);
    breaks.sort((a, b) => {
      return a.afterLevel - b.afterLevel;
    });
    let nextBreak = breaks.shift();
    let num = 0;
    let breakStart = 0;
    for (let blevel of blinds) {
      if (nextBreak && nextBreak.afterLevel == num) {
        nextBreak.breakStart = breakStart;
        structure.push(nextBreak);
        breakStart += nextBreak.levelTime * 60;
        nextBreak = breaks.shift();
      }
      breakStart += blevel.levelTime * 60;
      num++;
      blevel.num = num;
      blevel.kLevel = this.kBlinds(blevel);
      structure.push(blevel);
    }
    let elapsed_num: number = 0;
    for (let i = 0; i < structure.length; i++) {
      const level = structure[i];
      elapsed_num += level.levelTime;
      structure[i].elapsed = this.util.timeText(elapsed_num, true);
    }
    return structure;
  }
  private blindsSort(a: any, b: any) {
    function getBlindIndex(level: any) {
      let idx = level.sb + level.bb / 10;
      if (level.ante) idx += level.ante / 100;
      return idx;
    }
    return getBlindIndex(a) - getBlindIndex(b);
  }
  private kFormat(num: number) {
    if (num < 1000 || (num < 10000 && num % 1000 != 0)) return num.toString();
    else {
      let suffix = "";
      let divisor = 1;
      if (num >= 1000) {
        suffix = "K";
        divisor = 1000;
      }
      if (num >= 1000000) {
        suffix = "M";
        divisor = 1000000;
      }
      num /= divisor;
      if (num < 10) num = math.round(num, 2);
      else num = math.round(num, 1);
      return num.toString() + suffix;
    }
  }

  kBlinds(level: any) {
    let retValues = <any>{};
    if (
      (!level.ante && level.sb >= 10000) ||
      (level.ante && level.sb >= 4000)
    ) {
      retValues.sb = this.kFormat(level.sb);
      retValues.bb = this.kFormat(level.bb);
      if (level.ante) retValues.ante = this.kFormat(level.ante);
    } else {
      retValues.sb = level.sb;
      retValues.bb = level.bb;
      if (level.ante) retValues.ante = level.ante;
    }
    return retValues;
  }
  perTableChange(oldPerTable: number, newPerTable: number) {
    let tournUpdate: any = {};
    if (oldPerTable != newPerTable) {
      if (newPerTable > oldPerTable) {
        for (let t in this.latestTourn.tables) {
          let table: any = this.latestTourn.tables[t];
          if (!table.avail) {
            table.avail = [];
          }
          let start: number = oldPerTable;
          while (start < newPerTable) {
            if (table.seats.length <= start) {
              table.seats.push("-");
              table.avail.push(start);
            }
            start++;
          }
          tournUpdate["tables/" + t + "/avail"] = table.avail;
          tournUpdate["tables/" + t + "/seats"] = table.seats;
        }
      } else {
        for (let t in this.latestTourn.tables) {
          let table: any = this.latestTourn.tables[t];
          if (table.avail && Array.isArray(table.avail)) {
            let start: number = oldPerTable - 1;
            while (start >= newPerTable) {
              let found = table.avail.indexOf(start);
              if (found != -1) {
                table.avail.splice(found, 1);
                table.seats[start] = null;
              }
              start--;
            }
            tournUpdate["tables/" + t + "/avail"] = table.avail;
            tournUpdate["tables/" + t + "/seats"] = table.seats;
            // Seat locks on removed positions go with them.
            if (Array.isArray(table.locked)) {
              const locked = table.locked.filter(
                (s: number) => s < newPerTable,
              );
              tournUpdate["tables/" + t + "/locked"] = locked.length
                ? locked
                : null;
            }
          }
        }
      }
      this.tournamentAF.update(tournUpdate);
    }
  }
  changeParam(param: string, val: any) {
    // Safety net behind the disabled inputs: once the mystery bounty pool is
    // frozen, silently ignore writes to any prize-pool-affecting param.
    if (this.prizepoolParamLocked(param)) return;
    this.clearParamUpdateValues();
    if (param == "actPlayers") {
      if (!this.latestTourn.params.actPlayersDirty)
        this.addParamUpdate("actPlayersDirty", true);

      if (
        this.clubServ.latestClub.player_limit > 0 &&
        val > this.clubServ.latestClub.player_limit
      )
        val = this.clubServ.latestClub.player_limit;
    }
    if (
      param == "maxPlayers" &&
      this.clubServ.latestClub.player_limit > 0 &&
      val > this.clubServ.latestClub.player_limit
    )
      val = this.clubServ.latestClub.player_limit;

    if (param == "expPlayers" && !this.latestTourn.params.actPlayersDirty)
      this.addParamUpdate("actPlayers", val);
    if (param == "perTable" && this.latestLive.seated)
      this.perTableChange(this.latestTourn.params.perTable, val);
    if (
      param === "startingStack" &&
      val &&
      this.latestLive &&
      !this.latestLive.started &&
      this.latestTourn.players
    ) {
      var pUpdate: any = {};
      for (let p in this.latestTourn.players) {
        pUpdate["players/" + p + "/stack"] = val;
      }
      this.tournamentAF.update(pUpdate);
    }
    if (
      param == "tournamentType" &&
      val == "reentry_tournament" &&
      this.latestTourn.params.tournamentType != "reentry_tournament" &&
      this.latestLive.out
    ) {
      let pUpdate: any = {};
      for (let p in this.latestTourn.players) {
        if (this.latestTourn.players[p].finish) {
          this.latestTourn.players[p].reentryPossible = true;
          pUpdate["players/" + p + "/reentryPossible"] = true;
        }
      }
      this.tournamentAF.update(pUpdate);
    }
    if (
      param == "tournamentType" &&
      val != "reentry_tournament" &&
      this.latestTourn.params.tournamentType == "reentry_tournament" &&
      this.latestLive.out
    ) {
      let pUpdate: any = {};
      for (let p in this.latestTourn.players) {
        if (this.latestTourn.players[p].reentryPossible) {
          this.latestTourn.players[p].reentryPossible = null;
          pUpdate["players/" + p + "/reentryPossible"] = null;
        }
      }
      this.tournamentAF.update(pUpdate);
    }
    if (param === "payoutManual") {
      if (val) {
        // Lock places-paid to manual while payouts are manual, so the
        // hand-entered list can't desync from an auto-recalculated count.
        // Remember the prior mode to restore it if payouts go back to auto.
        this.addParamUpdate(
          "placesPaidManualBefore",
          !!this.latestTourn.params.calcPlacesPaidManual,
        );
        this.addParamUpdate("calcPlacesPaidManual", true);
      } else {
        this.addParamUpdate(
          "calcPlacesPaidManual",
          !!this.latestTourn.params.placesPaidManualBefore,
        );
      }
    }
    this.addParamUpdate(param, val);
    this.calcSmallBlind();
    this.calcStartingStack();
    this.calcRebuyChips();
    this.calcAddonChips();
    this.calcRebuys();
    this.calcAddons();
    this.setExpectedTotalChips();
    this.setPossibleBlinds();
    this.calcLevelTime();
    this.calcStructure();
    this.calcPlacesPaid();
    this.calcPrizepool();
    this.calcPayouts();
    this.updateParams();
    this.setActualTotalChips();
  }
  /**
   * Lazy backfill: when a tournament loads with a known-default chipset but
   * no chipColors, write the matching default colors + design to Firebase.
   * Fires once per tournament on first staff open. Doesn't touch
   * showChipColors (kept opt-in).
   */
  private rehydrateChipColors(results: any): void {
    if (!this.clubServ?.clubStaff) return;
    const params = results?.params;
    if (!params || !Array.isArray(params.chipset)) return;
    const existing = params.chipColors;
    if (Array.isArray(existing) && existing.some((c) => !!(c && c.p))) return;

    const key = JSON.stringify(params.chipset);
    const idx = (defaults.chipsets as number[][]).findIndex(
      (cs) => JSON.stringify(cs) === key,
    );
    if (idx < 0) return;

    const chipColors = (defaults as any).chipColors[idx];
    const chipDesign = (defaults as any).chipDesigns[idx];
    const update = { chipColors, chipDesign };
    this.latestTourn.params.chipColors = chipColors;
    this.latestTourn.params.chipDesign = chipDesign;
    this.paramsAF.update(update);
    // clubID lives on the tournament object, not on params — the old
    // params.clubID check was always false, so the mirror never got the
    // backfilled colors.
    if (this.tournID && results.clubID) {
      this.clubServ.updateTournParams(this.tournID, update, results.clubID);
    }
  }

  changeChipsetBundle(
    denoms: number[],
    colors: any[],
    design: string,
    showChipColors: boolean,
  ) {
    this.clearParamUpdateValues();
    this.addParamUpdate("chipColors", colors);
    this.addParamUpdate("chipDesign", design);
    this.addParamUpdate("showChipColors", showChipColors);
    this.addParamUpdate("chipset", denoms);
    this.calcSmallBlind();
    this.calcStartingStack();
    this.calcRebuyChips();
    this.calcAddonChips();
    this.calcRebuys();
    this.calcAddons();
    this.setExpectedTotalChips();
    this.setPossibleBlinds();
    this.calcLevelTime();
    this.calcStructure();
    this.calcPlacesPaid();
    this.calcPrizepool();
    this.calcPayouts();
    this.updateParams();
    this.setActualTotalChips();
  }
  /**
   * Clock panels dialog: which bottom-row info boxes this tournament shows
   * (hiddenInfoBoxes by box type, hiddenLeagueBoxes by leagueID) plus the
   * existing chip-colors opt-in. Display-only, so no blind/payout recalc
   * cascade — straight to updateParams(), which also mirrors the choice into
   * the user's profile defaults (new tournaments inherit it) and the club
   * lobby node. Empty maps are written as null so the key disappears.
   */
  updateClockPanels(panels: {
    hiddenInfoBoxes: { [type: string]: true };
    hiddenLeagueBoxes: { [leagueID: string]: true };
    showChipColors?: boolean;
  }) {
    const orNull = (m: any) => (m && Object.keys(m).length ? m : null);
    this.clearParamUpdateValues();
    this.addParamUpdate("hiddenInfoBoxes", orNull(panels.hiddenInfoBoxes));
    this.addParamUpdate("hiddenLeagueBoxes", orNull(panels.hiddenLeagueBoxes));
    if (panels.showChipColors !== undefined)
      this.addParamUpdate("showChipColors", panels.showChipColors);
    this.updateParams();
  }
  clearParamUpdateValues() {
    this.paramsUpdateValues = {};
  }
  addParamUpdate(param: string, val: any) {
    if (
      this.latestTourn &&
      this.latestTourn.params &&
      val != this.latestTourn.params[param]
    ) {
      this.paramsUpdateValues[param] = val;
      this.latestTourn.params[param] = val;
    }
  }
  calcSmallBlind() {
    if (!this.latestTourn.params.initSmallBlindManual) {
      this.addParamUpdate("initSmallBlind", this.latestTourn.params.chipset[0]);
    }
  }
  getStartingStack(params: any) {
    return params.initSmallBlind * this.getPerfectMult(params);
  }
  calcStartingStack() {
    if (!this.latestTourn.params.startingStackManual) {
      this.addParamUpdate(
        "startingStack",
        this.getStartingStack(this.latestTourn.params),
      );
    }
  }
  getPerfectMult(params: any): number {
    let ratio: number =
      (params.duration * 60) / params.expPlayers + params.duration * 3;
    if (isRebuyCapable(params.tournamentType)) ratio -= 2;
    let multSB: number;
    if (ratio < 20) multSB = 100;
    else if (ratio < 35) multSB = 200;
    else if (ratio < 45) multSB = 300;
    else if (ratio < 80) multSB = 400;
    else multSB = 600;
    return multSB;
  }
  calcRebuyChips() {
    if (!this.latestTourn.params.rebuyChipsManual) {
      this.addParamUpdate("rebuyChips", this.latestTourn.params.startingStack);
    }
  }
  getRoundedAddonChips(params: any) {
    if (this.profServ.latestProfile) {
      let rounder;
      for (rounder of params.chipset) {
        if (rounder >= params.startingStack * 0.1) break;
      }
      let addonchips: number = this.util.roundTo(
        params.startingStack *
          this.profServ.latestProfile.defaults.addonChipsRatio,
        rounder,
      );
      if (!addonchips) addonchips = 0;
      return addonchips;
    } else return 0;
  }
  calcAddonChips() {
    if (!this.latestTourn.params.addonChipsManual) {
      this.addParamUpdate(
        "addonChips",
        this.getRoundedAddonChips(this.latestTourn.params),
      );
    }
  }
  calcRebuys() {
    if (!this.latestTourn.params.rebuysManual) {
      this.addParamUpdate("rebuys", this.latestTourn.params.expPlayers);
    }
  }
  calcAddons() {
    if (!this.latestTourn.params.addonsManual) {
      this.addParamUpdate(
        "addons",
        Math.round(this.latestTourn.params.expPlayers * 0.75),
      );
    }
  }
  setExpectedTotalChips() {
    this.latestTourn.params.expTotalChips = this.totalChips(
      this.latestTourn.params.expPlayers,
      this.latestTourn.params.rebuys,
      this.latestTourn.params.addons,
      this.latestTourn.params.reentries || 0,
    );
  }
  setActualTotalChips() {
    let actTotalChips: number;
    if (this.latestTourn.params.manageRegistrations)
      actTotalChips = this.totalChips(
        this.getPlayersCount(),
        this.latestLive.rebuys,
        this.latestLive.addons,
        this.latestLive.reentries,
      );
    else
      actTotalChips = this.totalChips(
        this.getPlayersCount(),
        this.latestLive.rebuys,
        this.latestLive.addons,
        this.latestLive.reentries,
      );

    if (
      actTotalChips != this.latestLive.totalChips &&
      (actTotalChips || actTotalChips == 0)
    )
      this.liveAF.update({
        totalChips: actTotalChips,
      });
  }
  getPossibleBlinds(params: any) {
    const tmpChipset: number[] = params.chipset.slice();
    let possibleBlinds: any[] = [];
    let allPossibleBlinds: any[] = [];
    let finalSmallBlind: number = params.expTotalChips / 40;
    let maxVal: number = Math.max(...params.chipset);
    while (tmpChipset.length < 6) {
      if (maxVal.toString().startsWith("25")) {
        maxVal *= 4;
      } else {
        maxVal *= 5;
      }
      tmpChipset.push(maxVal);
    }
    const multiplier: any[] = [1, 2, 3, 4, 6, 8, 10, 12];
    for (let mult of multiplier)
      for (let chip of tmpChipset) {
        var blind = Math.round(mult * chip * 100) / 100;
        if (
          possibleBlinds.indexOf(blind) == -1 &&
          blind <= finalSmallBlind &&
          blind >= params.initSmallBlind
        )
          possibleBlinds.push(blind);
        if (
          allPossibleBlinds.indexOf(blind) == -1 &&
          blind >= params.initSmallBlind
        )
          allPossibleBlinds.push(blind);
      }
    this.util.numSort(possibleBlinds);
    this.util.numSort(allPossibleBlinds);
    return {
      possibleBlinds: possibleBlinds,
      allPossibleBlinds: allPossibleBlinds,
    };
  }
  setPossibleBlinds() {
    let blinds = this.getPossibleBlinds(this.latestTourn.params);
    this.possibleBlinds = blinds.possibleBlinds;
    this.allPossibleBlinds = blinds.allPossibleBlinds;
  }

  getLevelTime(params: any, possibleBlinds: any[]) {
    let numLevels: number = possibleBlinds.length;
    if (!params.levelTimeManual && numLevels) {
      if (params.antes) numLevels++; // add one lvel for antes tournaments because when antes are added blinds stay the same
      let idealLevelTime: number = (params.duration * 60) / numLevels;
      if (idealLevelTime <= 15) idealLevelTime = Math.ceil(idealLevelTime);
      else if (idealLevelTime <= 20)
        idealLevelTime = Math.ceil(idealLevelTime / 2) * 2;
      else if (idealLevelTime <= 60)
        idealLevelTime = Math.ceil(idealLevelTime / 5) * 5;
      else idealLevelTime = Math.ceil(idealLevelTime / 15) * 15;
      return idealLevelTime;
    } else return null;
  }
  calcLevelTime() {
    let lt = this.getLevelTime(this.latestTourn.params, this.possibleBlinds!);
    if (lt) this.addParamUpdate("levelTime", lt);
  }
  getManualRoundTo(prizepool: number, placesPaid: number): number {
    const params = this.latestTourn.params;
    if (params.rounding === "custom" && params.roundTo) return params.roundTo;
    const avgPayout = prizepool / placesPaid;
    if (avgPayout < 10) return 0.5;
    if (avgPayout < 20) return 1;
    if (avgPayout < 100) return 5;
    if (avgPayout < 200) return 10;
    if (avgPayout < 1000) return 25;
    if (avgPayout < 2500) return 50;
    if (avgPayout < 7500) return 100;
    if (avgPayout < 20000) return 250;
    if (avgPayout < 50000) return 500;
    return 1000;
  }
  roundManualAward(raw: number, prizepool: number, placesPaid: number): number {
    const params = this.latestTourn.params;
    if (params.payoutManualRounding === false) return math.round(raw, 2);
    const roundTo = this.getManualRoundTo(prizepool, placesPaid);
    const adjust = placesPaid > 13 && params.rounding === "auto";
    return this.util.roundAward(raw, roundTo, adjust);
  }
  updateManPayouts() {
    const prizepool = this.latestTourn.params.prizepool;
    const placesPaid = this.latestPayouts ? this.latestPayouts.length : 0;
    let sumOthers = 0;
    let firstKey: any = null;
    for (let p in this.latestPayouts) {
      if (Number(this.latestPayouts[p].place) === 1) {
        firstKey = p;
        continue;
      }
      if (this.latestPayouts[p].percent) {
        let award: number = this.roundManualAward(
          (prizepool * this.latestPayouts[p].percent) / 100,
          prizepool,
          placesPaid,
        );
        sumOthers += award;
        if (award != this.latestPayouts[p].award)
          this.payoutsAF.update(p, { award: award });
      } else {
        sumOthers += this.latestPayouts[p].award || 0;
      }
    }
    // 1st place absorbs the rounding remainder so awards sum to the prizepool.
    if (firstKey !== null) {
      let firstAward: number = this.util.cleanFloat(prizepool - sumOthers);
      if (firstAward != this.latestPayouts[firstKey].award)
        this.payoutsAF.update(firstKey, { award: firstAward });
    }
    setTimeout(() => {
      this.setLocalPayouts();
    });
  }
  updateParams() {
    if (this.paramsUpdateValues) {
      this.paramsAF.update(this.paramsUpdateValues);
      this.nodeUpdate(
        "profiles/" + this.profServ.uid + "/defaults",
        this.paramsUpdateValues,
      );
      this.clubServ.updateTournParams(
        this.tournID,
        this.paramsUpdateValues,
        this.latestTourn?.clubID,
      );
    }

    // Re-round manual payouts when the prizepool changes OR when a rounding
    // setting changes (rounding mode / roundTo / the rounding toggle), since
    // all of these affect the computed awards.
    const pv = this.paramsUpdateValues;
    if (
      this.latestTourn.params.payoutManual &&
      ("prizepool" in pv ||
        "rounding" in pv ||
        "roundTo" in pv ||
        "payoutManualRounding" in pv)
    )
      this.updateManPayouts();
    // Resize the manual payout list when places paid changes — auto mode
    // regenerates the whole table, but manual mode must add/remove rows itself.
    if (this.latestTourn.params.payoutManual && "placesPaid" in pv)
      this.syncManualPayoutPlaces();
  }
  syncManualPayoutPlaces() {
    if (!this.latestPayouts) return;
    const placesPaid = this.latestTourn.params.placesPaid || 0;
    const current = this.latestPayouts.length;
    if (placesPaid > current) {
      for (let i = current; i < placesPaid; i++) {
        this.payoutsAF.update(i.toString(), {
          place: i + 1,
          award: 0,
          percent: 0,
        });
      }
    } else if (placesPaid > 0 && placesPaid < current) {
      for (let i = current - 1; i >= placesPaid; i--) {
        this.payoutsAF.remove(i.toString());
      }
    }
    setTimeout(() => {
      this.setLocalPayouts();
    });
  }
  getLevels(params: any, possibleBlinds: any[], allPossibleBlinds: any[]) {
    let numLevels: number = Math.round(
      (params.duration * 60) / params.levelTime,
    );
    if (params.antes) numLevels--;
    let idealBlinds: any[] = [];
    let blindFactor: number;

    if (possibleBlinds.length <= numLevels) {
      numLevels = possibleBlinds.length;
      idealBlinds = possibleBlinds;
      blindFactor = 1.1;
    } else {
      let finalSmallBlind: number = possibleBlinds[possibleBlinds.length - 1];
      blindFactor = Math.pow(
        finalSmallBlind / possibleBlinds[0],
        1 / (numLevels - 1),
      );
      idealBlinds[0] = possibleBlinds[0];
      idealBlinds[numLevels - 1] = finalSmallBlind;
      for (let i = 1; i < numLevels - 1; i++) {
        idealBlinds[i] = idealBlinds[i - 1] * blindFactor;
      }
      for (let i = 1; i < numLevels - 1; i++) {
        for (var j = 1; j < possibleBlinds.length; j++) {
          if (possibleBlinds[j] < idealBlinds[i]) {
            var under = possibleBlinds[j];
          } else {
            var over = possibleBlinds[j];
            break;
          }
        }
        if (
          idealBlinds[i] - under <= over - idealBlinds[i] &&
          under > idealBlinds[i - 1]
        ) {
          idealBlinds[i] = under;
        } else {
          while (over <= idealBlinds[i - 1] && j < possibleBlinds.length - 1) {
            j++;
            over = possibleBlinds[j];
          }
          idealBlinds[i] = over;
        }
      }

      for (var doublePass = 0; doublePass < 2; doublePass++) {
        for (let i = 0; i < numLevels - 2; i++) {
          var center = (idealBlinds[i] + idealBlinds[i + 2]) / 2;
          for (let j = 1; j < possibleBlinds.length; j++) {
            if (possibleBlinds[j] < center) {
              under = possibleBlinds[j];
            } else {
              over = possibleBlinds[j];
              break;
            }
          }
          if (
            under != idealBlinds[i] &&
            (center == under ||
              idealBlinds[i + 1] - idealBlinds[i] >
                idealBlinds[i + 2] - idealBlinds[i + 1])
          ) {
            idealBlinds[i + 1] = under;
          }
          if (
            over != idealBlinds[i + 2] &&
            over != idealBlinds[i + 1] &&
            center == over
          ) {
            idealBlinds[i + 1] = over;
          }
        }
      }
    }
    // ADD 4 additional levels in case tourney goes long
    j = 0;
    if (blindFactor > 1.2) {
      blindFactor -= 0.1;
    }

    var extraBlindTarget = idealBlinds[idealBlinds.length - 1] * blindFactor;
    for (let i = numLevels; i < allPossibleBlinds.length; i++) {
      if (allPossibleBlinds[i] >= extraBlindTarget) {
        j++;
        extraBlindTarget = allPossibleBlinds[i] * blindFactor;
        idealBlinds.push(allPossibleBlinds[i]);
      }
      if (j > 3) break;
    }

    var antesArray = [];
    if (params.antes == true) {
      var possibleAntes = [];
      var currentAnte = 0;
      var chipSet = params.chipset;
      for (var ci = 0; ci < chipSet.length; ci++)
        chipSet[ci] = Number(chipSet[ci]);

      for (let j = 0; j < chipSet.length; j++) {
        var chipValue = chipSet[j];
        for (let i = 1; i < 5; i++) {
          if (chipValue * i > currentAnte) possibleAntes.push(chipValue * i);
        }
      }
      possibleAntes = possibleAntes.sort(function (a, b) {
        return a - b;
      });

      j = 0;

      for (let i = 0; i < idealBlinds.length; i++) {
        if (
          params.anteType === "big_blind_ante" ||
          params.anteType === "button_ante"
        )
          antesArray.push(idealBlinds[i] * 2);
        else if (possibleAntes[0] > idealBlinds[i] / 3) antesArray.push(0);
        else {
          var targetAnte = idealBlinds[i] / 3;
          while (possibleAntes[j] < targetAnte && j < possibleAntes.length) j++;
          if (j == 0) antesArray.push(possibleAntes[0]);
          else antesArray.push(possibleAntes[j - 1]);
        }
      }
    } else for (let i = 0; i < idealBlinds.length; i++) antesArray.push(0);

    if (params.antes && this.possibleBlinds && this.possibleBlinds.length > 0)
      this.addExtraAnteLevel(idealBlinds, antesArray);
    var newLevels: any = {};
    for (let i = 0; i < idealBlinds.length; i++)
      newLevels["l" + i] = {
        levelTime: params.levelTime,
        sb: idealBlinds[i],
        bb: idealBlinds[i] * 2,
        ante: antesArray[i],
        sort:
          idealBlinds[i] + (idealBlinds[i] * 2) / 100 + antesArray[i] / 1000,
        num: i + 1,
      };
    return newLevels;
  }
  calcStructure() {
    if (!this.latestTourn.params.structureManual) {
      if (this.latestTourn.params.levelTime)
        this.replaceLevels(
          this.getLevels(
            this.latestTourn.params,
            this.possibleBlinds!,
            this.allPossibleBlinds!,
          ),
        );
    }
  }
  addExtraAnteLevel(blinds: number[], antes: number[]) {
    let firstAnte: any;
    for (let a in antes) {
      if (antes[a]) {
        firstAnte = a;
        break;
      }
    }
    antes.splice(firstAnte, 0, 0);
    blinds.splice(firstAnte, 0, blinds[firstAnte]);
  }
  replaceLevels(newLevels: any) {
    if (this.latestLevels)
      for (let level of this.latestLevels) {
        if (level.afterLevel) {
          if (level.levelName) {
            newLevels[level.key] = {
              afterLevel: level.afterLevel,
              levelTime: level.levelTime,
              levelName: level.levelName,
            };
          } else {
            newLevels[level.key] = {
              afterLevel: level.afterLevel,
              levelTime: level.levelTime,
            };
          }
        }
      }
    this.nodeSet(this.levelsPath, newLevels);
  }
  getPlayersCount() {
    if (this.latestTourn && this.latestTourn.params) {
      if (this.latestTourn.params.manageRegistrations) {
        if (this.latestPlayers && this.latestPlayers.length > 0)
          return this.latestPlayers.length;
        else return 0;
      } else {
        return this.latestTourn.params.actPlayers || 0;
      }
    } else return 0;
  }
  calcPlacesPaid() {
    if (
      this.latestTourn &&
      this.latestTourn.params &&
      !this.latestTourn.params.calcPlacesPaidManual
    ) {
      let before: number = this.latestTourn.params.placesPaid;
      let play: number = this.getPlayersCount();
      let paid: number;
      if (play < 6) paid = 1;
      else if (play < 9) paid = 2;
      else if (play < 15) paid = 3;
      else if (play < 100) paid = Math.round(play / 5);
      else if (play > 500) paid = Math.round(play / 10);
      else {
        var tmpPercent = 20 - (play - 100) / 40;
        paid = Math.round((play * tmpPercent) / 100);
      }
      // Shootout: beyond the round-1 table winners, global finish order is
      // cross-table bust-time noise — never pay deeper than the table count
      // by default. Mirror of the server calcPlacesPaid cap.
      const sp = this.latestTourn.params;
      if (sp.tournamentType === "shootout" && sp.perTable) {
        paid = Math.min(paid, Math.max(1, Math.ceil(play / sp.perTable)));
      }
      if (before != paid) this.addParamUpdate("placesPaid", paid);
    }
  }
  calcPrizepool() {
    if (this.latestTourn.params.managePayouts) {
      let before: number = this.latestTourn.params.prizepool;
      let prizepool: number = 0;
      if (!this.latestTourn.playerCount) this.latestTourn.playerCount = 0;
      let playerCount: number = this.latestTourn.params.manageRegistrations
        ? this.latestTourn.playerCount
        : this.latestTourn.params.actPlayers;
      if (this.latestTourn.params.buyin) {
        if (this.latestTourn.params.manageRegistrations) {
          prizepool =
            (playerCount - this.latestLive.reentries) *
            this.latestTourn.params.buyin;
        } else {
          prizepool = playerCount * this.latestTourn.params.buyin;
        }
      }
      if (
        isRebuyCapable(this.latestTourn.params.tournamentType) &&
        this.latestTourn.live
      ) {
        if (!this.latestTourn.params.rebuyCost)
          this.latestTourn.params.rebuyCost = 0;
        if (!this.latestTourn.params.reentryCost)
          this.latestTourn.params.reentryCost = 0;

        if (!this.latestTourn.params.addonCost)
          this.latestTourn.params.addonCost = 0;

        prizepool +=
          this.latestTourn.live.rebuys * this.latestTourn.params.rebuyCost +
          this.latestTourn.live.addons * this.latestTourn.params.addonCost;
      }
      if (
        this.latestTourn.params.tournamentType === "reentry_tournament" &&
        this.latestLive.reentries
      ) {
        prizepool +=
          this.latestTourn.live.reentries * this.latestTourn.params.reentryCost;
      }
      if (!this.latestTourn.params.addedPrize)
        this.latestTourn.params.addedPrize = 0;
      prizepool += this.latestTourn.params.addedPrize;
      // Bounty carve: hold a portion of the pool out of the finishing-place
      // payouts. Fixed = flat per-knockout amount; Mystery = a % split of the
      // WHOLE pool (entry contributions + added prize); None = the sum of any
      // INDIVIDUAL bounty heads staff have placed on specific players. Persist
      // the carved bounty pool so the payouts UI / prize generator can read it.
      let bountyPool = 0;
      const bountyMode = this.bountyMode;
      if (bountyMode === "fixed") {
        // Uniform head × entries plus any individual overrides — delegated to
        // bountyPoolTotal so the carve matches the server calcBountyPool.
        bountyPool = this.bountyPoolTotal;
      } else if (bountyMode === "mystery") {
        const split =
          this.latestTourn.params.bountySplit == null
            ? 0.5
            : this.latestTourn.params.bountySplit;
        // prizepool here = contributions + addedPrize (the full pool).
        bountyPool = Math.max(0, Math.round(prizepool * split));
      } else {
        // None mode → individual bounties: Σ per-player head values.
        bountyPool = (this.latestPlayers || []).reduce(
          (s: number, p: any) => s + (p?.bountyValue || 0),
          0,
        );
      }
      // Floor at 0 — a bounty carve larger than the pool must never go negative.
      prizepool = Math.max(0, prizepool - bountyPool);
      if ((this.latestTourn.params.bountyPool || 0) != bountyPool)
        this.addParamUpdate("bountyPool", bountyPool);
      if (prizepool != before) {
        if (prizepool || prizepool == 0) {
          this.addParamUpdate("prizepool", prizepool);
          if (
            this.latestTourn.leagues &&
            this.latestLive &&
            this.latestLive.out
          )
            setTimeout(() => {
              for (let l in this.latestTourn.leagues)
                this.leagueServ.recalculate(l);
            });
        }
      }
    }
  }

  totalChips(ply: number, reb: number, add: number, reent: number) {
    let reentries = 0;
    if (reent) reentries = reent;
    let totalChips: number =
      (ply - reentries) * this.latestTourn.params.startingStack;
    if (isRebuyCapable(this.latestTourn.params.tournamentType))
      totalChips += add * this.latestTourn.params.addonChips;
    if (this.latestTourn.params.tournamentType === "rebuy_tournament")
      totalChips += reb * this.latestTourn.params.rebuyChips;
    if (this.latestTourn.params.tournamentType === "reentry_tournament") {
      totalChips += reentries * this.latestTourn.params.reentryChips;
    }
    if (
      this.latestTourn.params.manageRegistrations &&
      this.latestTourn.players
    ) {
      for (var p in this.latestTourn.players) {
        let player: any = this.latestTourn.players[p];
        if (player.bonusChips) totalChips += player.bonusChips;
      }
    }
    return totalChips;
  }
  isDuplicateBreak(level: any) {
    if (!level.afterLevel || level.afterLevel == 0) return false;
    for (let lvl of this.latestLevels) {
      if (lvl.afterLevel && lvl.afterLevel == level.afterLevel) return true;
    }
    return false;
  }
  createLevel(level: any) {
    if (!this.isDuplicateBreak(level)) this.levelsAF.push(level);
  }
  deleteLevel(key: any) {
    this.levelsAF.remove(key);
  }
  updateLevel(level: any) {
    let tempKey: string = level.key;
    delete level.key;
    this.levelsAF.update(tempKey, level);
  }
  getTemplateParams() {
    let params: any = this.util.copyObject(this.latestTourn.params);
    params.seated = null;
    params.undoIdx = null;
    return params;
  }
  saveTemplateMessage() {
    this.translate.get(["TEMPLATE_SAVED"]).subscribe((res: any) => {
      let saveMessage = res["TEMPLATE_SAVED"];
      this.snackBar.open(saveMessage, undefined, {
        duration: 3000,
      });
    });
  }
  saveTemplate(key: string) {
    let update = {
      params: this.getTemplateParams(),
      structure: this.util.getLevelsStructure(this.latestLevels),
    };

    this.profServ.templatesAF.update(key, update).then(() => {
      this.saveTemplateMessage();
    });
  }
  deleteTemplate(key: string) {
    this.profServ.templatesAF.remove(key);
  }
  createTemplate(name: string) {
    let template = {
      name: name,
      params: this.getTemplateParams(),
      structure: this.util.getLevelsStructure(this.latestLevels),
    };
    this.profServ.templatesAF.push(template).then(() => {
      this.saveTemplateMessage();
    });
  }
  async loadTemplate(template: any) {
    if (template && template.params && template.structure) {
      if (!template.params.tournamentType) {
        if (template.params.rebuyTournament)
          template.params.tournamentType = "rebuy_tournament";
        else template.params.tournamentType = "freezeout";
        delete template.params.rebuyTournament;
      }
      if (typeof template.params.chipset == "string") {
        // remove trailing comma if exists
        if (template.params.chipset.endsWith(",")) {
          template.params.chipset = template.params.chipset.slice(0, -1);
        }
        template.params.chipset = template.params.chipset
          .split(",")
          .map((chip: string) => parseFloat(chip));
      }
      await this.nodeUpdate(this.paramsPath, template.params);
      await this.nodeSet(this.levelsPath, template.structure);
    }
  }
  changeUndoIdx(delta: number) {
    this.nodeTransaction(
      "tournaments/" + this.tournID + "/live/undoIdx",
      (count) => {
        if (count) return count + delta;
        else return delta;
      },
    );
    if (this.latestLive && this.latestLive.undoIdx)
      this.latestLive.undoIdx += delta;
    else this.latestLive.undoIdx = delta;
    return this.latestLive.undoIdx;
  }
  needsNameAppended(type: string) {
    let needAppending = [
      "REGISTER",
      "UNREGISTER",
      "ELIMINATION",
      "REBUY",
      "ADDON",
    ];
    return needAppending.indexOf(type) != -1;
  }
  needsUndo(type: string) {
    let needsUndoFunc = [
      "UNREGISTER",
      "DRAW_SEATS",
      "ADD_TABLE",
      "BREAK_TABLE",
      "BALANCE_TABLES",
      "MANUAL_SEAT_CHANGE",
    ];
    let undos: any = {
      dependent: false,
      independent: false,
    };
    if (this.latestTourn && this.latestTourn.params && this.latestLive) {
      if (
        type == "REBUY" ||
        type == "ADDON" ||
        type == "REENTRY" ||
        type == "MERGE_TOURNAMENT"
      )
        undos.independent = true;
      else if (type == "REGISTER" && this.latestLive.seated)
        undos.dependent = true;
      else if (type == "REGISTER" && !this.latestLive.seated)
        undos.independent = true;
      else if (
        type == "ELIMINATION" &&
        this.latestTourn.params.manageRegistrations
      )
        undos.dependent = true;
      else if (
        type == "ELIMINATION" &&
        !this.latestTourn.params.manageRegistrations
      )
        undos.independent = true;
      else undos.dependent = needsUndoFunc.indexOf(type) != -1;
    }
    return undos;
  }
  logEntry(type: string, params?: any, tournIDOverride?: string) {
    if (type) {
      // createTournament logs against the freshly-created tournID before it
      // becomes the active one, so allow an explicit target.
      const targetId = tournIDOverride || this.tournID;
      let entry: any = {
        type: type,
        timestamp: serverTimestamp(),
      };
      let th = this;
      this.translate
        .get([
          "CREATE_TOURNAMENT",
          "REGISTER",
          "UNREGISTER",
          "DRAW_SEATS",
          "ADD_TABLE",
          "BREAK_TABLE",
          "BALANCE_TABLES",
          "REBUY",
          "REENTRY",
          "ADDON",
          "ELIMINATION",
          "REPLAY_TOURNAMENT",
          "MANUAL_SEAT_CHANGE",
          "MERGE_TOURNAMENT",
        ])
        .subscribe((res: any) => {
          entry.message = res[type];
          if (this.needsNameAppended(entry.type) && params.name) {
            entry.message += ": " + params.name;
          }
          if (params) entry.undoParams = params;

          entry.undos = this.needsUndo(entry.type);
          if (entry.undos.dependent) entry.undoIdx = this.changeUndoIdx(1);
          runInInjectionContext(this.injector, () =>
            push(ref(this.mdb, `logs/tournaments/${targetId}`), entry),
          );
        });
      /*		if(type == "CREATE_TOURNAMENT" || type == "REPLAY_TOURNAMENT")
            params = this.latestTourn.params; */
      this.tracking.track(type, params);
    }
  }
  getPlayersRemaining() {
    if (this.latestTourn && this.latestLive) {
      return this.getPlayersCount() - this.latestLive.out;
    } else return null;
  }

  // players/{uid}/payout is SERVER-authoritative: eliminatePlayer writes it
  // with the elimination (same op as finish), and the onPayoutTableChange
  // RTDB trigger reassigns finished players' payouts whenever /payouts/{id}
  // changes (op recalcs, param cascade, manual award edits). The old client
  // writer (assignPayoutToPlayers, called from updateStacks) ran on every
  // device on every /payouts emit and clobbered server-written payouts with
  // null when its listener-fed player data was stale. Payout is the
  // prize-pool placement award only — bounty winnings live separately in
  // player.bountyWinnings (see bountyWon()).

  // Swap two players' finishing positions (server op, logged as SWAP_FINISH).
  // Resolves to the callable's data: { success, error? }.
  swapFinish(uidA: string, uidB: string): Promise<any> {
    this.tracking.track("SWAP_FINISH");
    return this.api
      .swapFinish(this.tournID, uidA, uidB)
      .then((result: any) => (result && result.data) || { success: false });
  }
  elimination(playerID?: string, killerID?: string, targetTournID?: string) {
    // Player-specific elimination always goes server-side for atomicity.
    // Returns the server result (incl. bountyPending) so the caller can offer a
    // mystery-bounty draw.
    if (playerID) {
      return this.serverSideElimination(playerID, killerID, targetTournID);
    }

    // Playerless "count remaining players" mode for lightweight tournaments
    // that track only actPlayers (no individual player records to eliminate).
    // Server op: transactional live/out++ (two devices no longer lose a
    // count), final-count detection + clock stop + finishTime + club mirror,
    // and a dependent-undo log entry (counted eliminations were previously
    // not undoable at all).
    this.loadingEliminationPlayer = true;
    return this.api
      .countElimination(this.tournID)
      .then((result: any) => {
        const data = result && result.data;
        if (!data || !data.success) {
          console.error("countElimination returned:", data);
          return;
        }
        if (data.tournamentFinished) this.tournamentFinished$.next();
        this.api.sendEvent(
          "EVT_PLAYER_ELIMINATED",
          this.profServ.latestProfile,
        );
      })
      .catch((err: any) => {
        console.error("countElimination error:", err);
      })
      .finally(() => {
        this.loadingEliminationPlayer = false;
      });
  }

  // Server-side elimination for tournaments that manage registrations
  serverSideElimination(
    playerID?: string,
    killerID?: string,
    targetTournID?: string,
  ) {
    if (!playerID) {
      console.error("Server-side elimination requires a playerID");
      return;
    }

    this.loadingEliminationPlayer = true;

    // Split parent: the op routes to the player's child tournament — capture
    // the routed target so the success handler below only touches THIS
    // tournament's clock when the finish actually belongs to it.
    const opTarget = this.opTournID(playerID, targetTournID);

    return this.api
      .eliminatePlayer(opTarget, playerID, killerID)
      .then((result: any) => {
        this.loadingEliminationPlayer = false;

        const data = result.data;

        if (data.success) {
          // Show warnings if any post-transaction operations failed
          if (data.warnings && data.warnings.length > 0) {
            console.warn("Elimination completed with warnings:", data.warnings);
            // Optionally show a warning to the user
            this.snackBar.open(
              this.translate.instant("ELIMINATION_SUCCESS_WITH_WARNINGS"),
              this.translate.instant("OK"),
              { duration: 5000 },
            );
          }

          // finish AND payout are written server-side with the elimination;
          // the realtime listener refreshes local state. No updateStacks()
          // here: its old payout-assignment half clobbered the just-written
          // payout with null when latestPlayers hadn't yet caught up to the
          // new finish (the eliminated-but-no-payout bug), and its stack half
          // is a no-op mid-tournament. Payout table changes are reconciled
          // onto players by the onPayoutTableChange trigger server-side.
          // checkBalance() is notification-only (no writes).
          this.checkBalance();

          // If tournament finished, handle UI updates
          // Winner info on club tournament summary is set by the Cloud Function
          // — but ONLY when the finish belongs to the tournament this service
          // is driving. A child finishing from a Split parent's roster must
          // NOT pause the parent (whose clock fan-out would freeze every
          // sibling); the hub bar badge reflects the child finish on its own.
          if (data.tournamentFinished && opTarget === this.tournID) {
            this.stopTimer();
            this.tournamentFinished$.next();
          }
          // Resolve with the server result so the knockout dialog can offer a
          // mystery-bounty draw (data.bountyPending / bountyWinnerSweep).
          return data;
        } else {
          // Unexpected response format
          console.error("Unexpected response from eliminatePlayer:", data);
          this.snackBar.open(
            this.translate.instant("ELIMINATION_FAILED"),
            this.translate.instant("OK"),
            { duration: 5000 },
          );
        }
      })
      .catch((error: any) => {
        this.loadingEliminationPlayer = false;
        console.error("Error calling eliminatePlayer function:", error);

        // Map error codes to user-friendly messages
        let errorMessage = this.translate.instant("ELIMINATION_FAILED");

        if (error.message) {
          // Use the specific error message from the function
          if (error.message.includes("Tournament not found")) {
            errorMessage = this.translate.instant("TOURNAMENT_NOT_FOUND");
          } else if (error.message.includes("Player not found")) {
            errorMessage = this.translate.instant("PLAYER_NOT_FOUND");
          } else if (error.message.includes("already been eliminated")) {
            errorMessage = this.translate.instant("PLAYER_ALREADY_ELIMINATED");
          } else if (error.message.includes("manage player registrations")) {
            errorMessage = this.translate.instant("ELIMINATION_NOT_SUPPORTED");
          } else if (error.message.includes("concurrent modification")) {
            errorMessage = this.translate.instant(
              "ELIMINATION_CONCURRENT_ERROR",
            );
          } else {
            // For any other errors, show the raw message
            errorMessage = error.message;
          }
        }

        this.snackBar.open(errorMessage, this.translate.instant("OK"), {
          duration: 7000,
        });
      });
  }

  // Draw the mystery bounty for a knockout (server-authoritative). `playerID`
  // is the eliminated player whose bounty is recovered; `killerID` wins the
  // prize. Returns the server result { drawn, value, prizeIndex } so the caller
  // can trigger the reveal. (MYSTERY_DRAW logging + undo land in the undo phase.)
  // Single draw (knockout "Draw now") = a batch of one.
  drawMysteryBounty(playerID: string, killerID: string): Promise<any> {
    return this.drawMysteryBountyBatch([{ playerID, killerID }]);
  }

  // Draw a batch of pending bounties in ONE cloud call, played as ONE continuous
  // reveal ceremony. Same model as Break Table: publish to the shared
  // clockOverlay node (fullscreen clocks render it) AND open a local reveal
  // dialog on this (initiating) device; both read the node so they stay synced.
  drawMysteryBountyBatch(
    pairs: { playerID: string; killerID: string }[],
  ): Promise<any> {
    const tourn = this.latestTourn;
    const valid = (pairs || []).filter((p) => p && p.playerID && p.killerID);
    if (!tourn || !valid.length) return Promise.resolve(null);
    // Safety net behind the disabled draw buttons: don't snapshot a pool whose
    // manual prizes don't sum to the bounty pool. Every client draw path funnels
    // through here (drawMysteryBounty / drawAllForKiller / drawAllPending).
    if (this.bountyDrawBlocked) return Promise.resolve(null);
    // Spinner for the server round-trip (the warm dispatcher can take a few
    // seconds) so there's feedback before the reveal ceremony opens.
    this.loadingBountyDraw = true;
    return this.api
      // opTournID: a draw launched from a Split parent's roster lands on the
      // child that owns the pool (the reveal ceremony still plays here).
      .drawMysteryBountyBatch(this.opTournID(valid[0]?.playerID), valid)
      .then((result: any) => {
        this.loadingBountyDraw = false;
        const data = result.data;
        if (data && data.drawn && data.draws && data.draws.length) {
          this.clockOverlay.showMysteryBounty(data.draws, data.order || []);
          const ref = this.dialog.open(BountyRevealDialogComponent, {
            width: "100vw",
            height: "100dvh",
            maxWidth: "100vw",
            maxHeight: "100dvh",
            panelClass: "bounty-reveal-panel",
            hasBackdrop: false,
            disableClose: true,
          });
          // Resolve only when the ceremony finishes (serializes any follow-on draw).
          return new Promise((resolve) =>
            ref.afterClosed().subscribe(() => resolve(data)),
          );
        }
        return data;
      })
      .catch((error: any) => {
        this.loadingBountyDraw = false;
        console.error("Error calling drawMysteryBountyBatch:", error);
        this.snackBar.open(
          error?.message || this.translate.instant("BOUNTY_DRAW_FAILED"),
          this.translate.instant("OK"),
          { duration: 7000 },
        );
        return null;
      });
  }

  // Unmanaged ("count only") re-entry — server op, same family as rebuy/
  // addon: transactional live/reentries++ with the prizepool/payout recalc
  // and its own log entry. (Managed re-entry is just a registration and goes
  // through tournamentRegister.) Was a client read-increment-write followed
  // by the client param cascade.
  reentry() {
    this.loadingChipOp = true;
    this.api
      .tournamentReentry(this.tournID)
      .then((result: any) => {
        const data = result && result.data;
        if (!data || !data.success) {
          console.error("tournamentReentry returned:", data);
          return;
        }
        this.api.sendEvent(
          "EVT_PLAYER_REENTERED",
          this.profServ.latestProfile,
        );
      })
      .catch((err: any) => {
        console.error("tournamentReentry error:", err);
      })
      .finally(() => {
        this.loadingChipOp = false;
      });
  }
  rebuy(playerID?: string, targetTournID?: string) {
    this.loadingChipOp = true;
    this.api
      .tournamentRebuy(this.opTournID(playerID, targetTournID), playerID)
      .then((result: any) => {
        const data = result && result.data;
        if (!data || !data.success) {
          console.error("tournamentRebuy returned:", data);
          return;
        }
        this.api.sendEvent("EVT_PLAYER_REBOUGHT", this.profServ.latestProfile);
      })
      .catch((err: any) => {
        console.error("tournamentRebuy error:", err);
      })
      .finally(() => {
        this.loadingChipOp = false;
      });
  }
  addon(playerID?: string, targetTournID?: string) {
    this.loadingChipOp = true;
    this.api
      .tournamentAddon(this.opTournID(playerID, targetTournID), playerID)
      .then((result: any) => {
        const data = result && result.data;
        if (!data || !data.success) {
          console.error("tournamentAddon returned:", data);
          return;
        }
        this.api.sendEvent("EVT_PLAYER_ADDON", this.profServ.latestProfile);
      })
      .catch((err: any) => {
        console.error("tournamentAddon error:", err);
      })
      .finally(() => {
        this.loadingChipOp = false;
      });
  }
  // Unmanaged player count ± (the +/- buttons). Server op: transactional
  // actPlayers adjustment + prizepool/payout recalc + club/payouts mirrors.
  // Was changeParam(actPlayers ± 1) — a read-modify-write off listener state
  // that lost updates when two devices tapped concurrently.
  adjustActPlayers(delta: number) {
    this.loadingChipOp = true;
    this.api
      .adjustActPlayers(this.tournID, delta)
      .then((result: any) => {
        const data = result && result.data;
        if (!data || !data.success) {
          console.error("adjustActPlayers returned:", data);
        }
      })
      .catch((err: any) => {
        console.error("adjustActPlayers error:", err);
      })
      .finally(() => {
        this.loadingChipOp = false;
      });
  }
  toggleSeatLock(playerID: string) {
    if (playerID && this.latestTourn.players[playerID]) {
      const currentLockStatus =
        this.latestTourn.players[playerID].locked || false;
      this.playersAF.update(playerID, {
        locked: !currentLockStatus,
      });
    }
  }
  isLocked(uid: string): boolean {
    return this.latestTourn?.players[uid]?.locked || false;
  }
  // Empty-seat locks live on the table (tables/{i}/locked = seat indices),
  // separate from the per-player `locked` flag above. The lock is positional:
  // a manual move onto a locked seat is allowed and doesn't clear it.
  isEmptySeatLocked(tableIdx: number, seatIdx: number): boolean {
    const locked = this.latestTourn?.tables?.[tableIdx]?.locked;
    return Array.isArray(locked) && locked.indexOf(seatIdx) !== -1;
  }
  toggleEmptySeatLock(tableIdx: number, seatIdx: number) {
    const table = this.latestTourn?.tables?.[tableIdx];
    if (!table) return;
    const locked: number[] = Array.isArray(table.locked)
      ? table.locked.slice()
      : [];
    const pos = locked.indexOf(seatIdx);
    if (pos === -1) locked.push(seatIdx);
    else locked.splice(pos, 1);
    locked.sort((a, b) => a - b);
    const update: any = {};
    update["tables/" + tableIdx + "/locked"] = locked.length ? locked : null;
    this.tournamentAF.update(update);
  }
  unregister(uid: string, undoAction?: boolean, targetTournID?: string) {
    // Mystery bounty pool frozen → no manual unregister (the field can't shrink
    // once bounties are drawn). Undo still passes through (server-mirrored).
    if (this.bountyPoolLocked && !undoAction) return;
    if (!uid) {
      this.api.appLog(
        "tournament.service",
        Date.now(),
        "unregister",
        "invalid uid",
        { tournID: this.tournID },
      );
      return;
    }
    this.api
      .tournamentUnregister(this.opTournID(uid, targetTournID), uid, undoAction)
      .then(() => {
        this.loadingUnRegisterPlayer = false;
        this.clubServ.loadingID = "";
      });
  }
  calcPayouts() {
    let params = this.latestTourn.params;
    if (!params.payoutManual && params.prizepool && params.managePayouts) {
      if (params.placesPaid) {
        var golden = 1.618034;
        var lastFib;
        var percentPaid;
        var initP5s;
        var lastP5s;
        var payoutsP5s;
        var ratioP5s;
        var sumP5s = 0;
        var tmpPayout;
        var p;
        var remPrizepool;
        var tmpAward;
        var sumAward;
        var sumPercent;
        var payoutsArray;
        var roundTo = 0.01;
        let buyin = params.buyin;
        let playersCount = this.getPlayersCount();
        if (buyin == 0 && playersCount) buyin = params.prizepool / playersCount;

        payoutsArray = [];

        var avgPayout = params.prizepool / params.placesPaid;
        if (params.rounding == "custom" && params.roundTo)
          roundTo = params.roundTo;
        else if (avgPayout < 10) roundTo = 0.5;
        else if (avgPayout < 20) roundTo = 1;
        else if (avgPayout < 100) roundTo = 5;
        else if (avgPayout < 200) roundTo = 10;
        else if (avgPayout < 1000) roundTo = 25;
        else if (avgPayout < 2500) roundTo = 50;
        else if (avgPayout < 7500) roundTo = 100;
        else if (avgPayout < 20000) roundTo = 250;
        else if (avgPayout < 50000) roundTo = 500;
        else roundTo = 1000;

        lastFib =
          params.prizepool /
          ((Math.pow(golden, params.placesPaid) - 1) * golden);
        percentPaid = (params.placesPaid / (params.prizepool / buyin)) * 100;

        if (percentPaid < 17) initP5s = (buyin * 24) / percentPaid - lastFib;
        else initP5s = buyin * 1.5 - lastFib;
        lastP5s = 100 / Math.pow(params.placesPaid, 0.6);
        ratioP5s = initP5s / lastP5s;
        payoutsP5s = [];

        for (p = 0; p < params.placesPaid; p++) {
          tmpPayout = (100 / Math.pow(p + 1, 0.6)) * ratioP5s;
          sumP5s = sumP5s + tmpPayout;
          payoutsP5s.push(tmpPayout);
        }

        if (sumP5s > params.prizepool) {
          ratioP5s = (ratioP5s * params.prizepool * 0.8) / sumP5s;
          sumP5s = 0;
          payoutsP5s = [];
          for (p = 0; p < params.placesPaid; p++) {
            tmpPayout = (100 / Math.pow(p + 1, 0.6)) * ratioP5s;
            sumP5s = sumP5s + tmpPayout;
            payoutsP5s.push(tmpPayout);
          }
        }

        remPrizepool = params.prizepool - sumP5s;
        tmpPayout =
          remPrizepool / ((Math.pow(golden, params.placesPaid) - 1) * golden);
        tmpAward = tmpPayout + payoutsP5s[params.placesPaid - 1];
        if (params.placesPaid > 13 && params.rounding == "auto")
          tmpAward = this.util.roundAward(tmpAward, roundTo, true);
        else tmpAward = this.util.roundAward(tmpAward, roundTo, false);
        sumAward = tmpAward;

        if (params.placesPaid == 1)
          payoutsArray.push({
            place: params.placesPaid,
            award: params.prizepool,
          });
        else
          payoutsArray.push({
            place: params.placesPaid,
            award: tmpAward,
          });

        if (params.placesPaid > 1) {
          for (p = params.placesPaid - 2; p > 0; p--) {
            tmpPayout = tmpPayout * golden;
            tmpAward = tmpPayout + payoutsP5s[p];
            if (params.placesPaid > 13 && params.rounding == "auto")
              tmpAward = this.util.roundAward(tmpAward, roundTo, true);
            else tmpAward = this.util.roundAward(tmpAward, roundTo, false);

            sumAward = sumAward + tmpAward;

            payoutsArray.push({
              place: p + 1,
              award: tmpAward,
            });
          }
          tmpAward = this.util.cleanFloat(params.prizepool - sumAward);

          payoutsArray.push({
            place: 1,
            award: tmpAward,
          });
        }

        // Place order (index i === place i+1) — the manual editor addresses
        // rows by index. An award sort left tied adjacent places reversed.
        payoutsArray.sort((a, b) => a.place - b.place);
        payoutsArray = this.setPayoutPercents(payoutsArray);
        this.nodeSet("payouts/" + this.tournID, payoutsArray);
      } else {
        this.nodeSet("payouts/" + this.tournID, null);
      }
    }
  }
  setPayoutPercents(payoutsArray: any[]) {
    for (let p in payoutsArray) {
      payoutsArray[p].percent = math.round(
        (payoutsArray[p].award / this.latestTourn.params.prizepool) * 100,
        2,
      );
    }
    return payoutsArray;
  }
  // Pre-start stack maintenance only. Payout assignment was removed from here:
  // players/{uid}/payout is server-authoritative now (eliminatePlayer +
  // onPayoutTableChange trigger) — see the note above elimination().
  updateStacks(uid?: string) {
    let update: any = {};
    if (
      this.latestTourn &&
      this.latestTourn.params &&
      this.latestTourn.params.manageRegistrations &&
      this.latestPlayers
    ) {
      for (let player of this.latestPlayers) {
        const stack =
          this.latestTourn.params.tournamentType === "reentry_tournament" &&
          player.baseuid
            ? this.latestTourn.params.reentryChips
            : this.latestTourn.params.startingStack;

        if (player.uid) {
          var bought =
            stack +
            player.rebuys * this.latestTourn.params.rebuyChips +
            player.addon * this.latestTourn.params.addonChips +
            (player.bonusChips || 0);
          if (!this.latestLive.started && !player.manStack)
            update["players/" + player.uid + "/stack"] = bought;
        } else
          this.api.appLog(
            "tournament.service",
            Date.now(),
            "updateStacks",
            "no uid on player object",
            {
              tournID: this.tournID,
            },
          );
      }
      if (Object.keys(update).length) {
        this.tournamentAF.update(update).then(() => {
          this.setActualTotalChips();
        });
      } else {
        this.setActualTotalChips();
      }
    }
  }
  // Notification-only. The needTableBreak / needTableBalance / needFinalTableDraw
  // flags are computed and written SERVER-SIDE (recomputeSeatingFlags in
  // bvapi.js) — clients only read them. This method no longer writes those
  // flags (that client-write was the cause of the mixed-version flicker); it
  // just recomputes them locally to decide whether to pop the break/balance/
  // draw notification snackbars.
  checkBalance() {
    if (
      this.latestLive &&
      this.latestTourn &&
      this.latestLive.seated &&
      this.latestTourn.players &&
      this.latestTourn.tables &&
      this.getPlayersCount() > this.latestLive.out
    ) {
      var remaining =
        Object.keys(this.latestTourn.players).length - this.latestLive.out;
      // Exclude broke (tombstoned) tables from the local count, matching the
      // server formula.
      var activeTables = this.latestTourn.tables.filter(
        (tbl: any) => tbl && !tbl.broke,
      );
      var numTables = activeTables.length;
      if (numTables > 1) {
        if (this.isShootout) {
          // Shootout: break/balance/final-table prompts never apply — the
          // local formulas below would misfire mid-bracket. The one prompt is
          // the round advance, keyed on the SERVER flag. Not gated on
          // "running": the clock is commonly paused right as the last table
          // finishes.
          this.notifiedBreak = false;
          this.notifiedBalance = false;
          this.notifiedFinalTable = false;
          const needAdvance = !!this.latestLive.needShootoutAdvance;
          if (needAdvance && !this.notifiedShootoutAdvance) {
            this.notifiedShootoutAdvance = true;
            this.translate
              .get(["SHOOTOUT_ALL_TABLES_COMPLETE", "SHOOTOUT_ADVANCE"])
              .subscribe((res: any) => {
                const snackRef = this.snackBar.open(
                  res["SHOOTOUT_ALL_TABLES_COMPLETE"],
                  res["SHOOTOUT_ADVANCE"],
                  { duration: 8000 },
                );
                snackRef.onAction().subscribe(() => {
                  this.advanceShootoutRound();
                });
              });
          }
          if (!needAdvance) this.notifiedShootoutAdvance = false;
          return;
        }
        let extraSeats = this.latestTourn.params.extraSeats;
        if (!extraSeats) extraSeats = 0;
        //Check to see if we are at the final table
        let finalTable =
          remaining + extraSeats <=
          this.latestTourn.params.numberPlayersFinalTable;

        // Check to see if a table needs to break

        var reqTables = Math.ceil(
          (remaining + extraSeats) / this.latestTourn.params.perTable,
        );
        let needBreak =
          reqTables < numTables && !this.latestLive.needFinalTableDraw;

        // Check to see if tables need to be balanced
        var maxCount = 0;
        var minCount = 99;
        for (var i = 0; i < numTables; i++) {
          var tmpCnt = activeTables[i].count;
          if (tmpCnt > maxCount) maxCount = tmpCnt;
          if (tmpCnt < minCount) minCount = tmpCnt;
        }
        let needBalance: boolean =
          maxCount - minCount > 1 && !needBreak && !finalTable;
        const running =
          !this.lastActionIsRegister &&
          this.latestTourn.tournStatus === "Running";

        // Each notification is EDGE-triggered: pop only when its need newly
        // arises, then arm again once the need clears — so unrelated tournament
        // emits (a bounty draw, a stack edit) don't re-pop a still-pending toast.
        if (needBreak && running && !this.notifiedBreak) {
          this.notifiedBreak = true;
          this.translate
            .get(["NEED_TABLE_BREAK", "BREAK_TABLE"])
            .subscribe((res: any) => {
              let balanceRef = this.snackBar.open(
                res["NEED_TABLE_BREAK"],
                res["BREAK_TABLE"],
                { duration: 5000 },
              );
              balanceRef.onAction().subscribe(() => {
                const moves = this.dialog.open(PlayerMovesComponent, {
                  data: { mode: "break" },
                  width: "min(680px, 95vw)",
                });
                moves.afterClosed().subscribe(() => this.clockOverlay.dismiss());
                // Close the empty moves shell if the op is rejected (e.g.
                // ALL_SEATS_LOCKED) so it isn't left behind the error modal.
                this.breakTable().then((ok) => {
                  if (!ok) moves.close();
                });
              });
            });
        }
        if (!needBreak) this.notifiedBreak = false;

        if (needBalance && running && !this.notifiedBalance) {
          this.notifiedBalance = true;
          this.translate
            .get(["UNBALANCED", "BALANCE_TABLES"])
            .subscribe((res: any) => {
              let balanceRef = this.snackBar.open(
                res["UNBALANCED"],
                res["BALANCE_TABLES"],
                { duration: 5000 },
              );
              balanceRef.onAction().subscribe(() => {
                const moves = this.dialog.open(PlayerMovesComponent, {
                  data: { mode: "balance" },
                  width: "min(680px, 95vw)",
                });
                moves.afterClosed().subscribe(() => this.clockOverlay.dismiss());
                this.balanceTables().then((ok) => {
                  if (!ok) moves.close();
                });
              });
            });
        }
        if (!needBalance) this.notifiedBalance = false;

        if (
          finalTable &&
          this.latestPlayers &&
          running &&
          !this.notifiedFinalTable
        ) {
          this.notifiedFinalTable = true;
          this.translate
            .get(["FINAL_TABLE_DRAW", "DRAW_SEATS"])
            .subscribe((res: any) => {
              let balanceRef = this.snackBar.open(
                res["FINAL_TABLE_DRAW"],
                res["DRAW_SEATS"],
                { duration: 5000 },
              );
              balanceRef.onAction().subscribe(() => {
                this.drawSeats();
              });
            });
        }
        if (!finalTable) this.notifiedFinalTable = false;
        // (Flags are written server-side now; nothing to persist here.)
        //if (!needBalance && !needBreak && !finalTable)
        //	this.snackBar.dismiss();
      }
    }
    this.lastActionIsRegister = false;
  }
  /**
   * Read-only seat-availability check, safe to call from a template binding.
   * Mirrors the core decision in seatAvailable() but has NO side effects — it
   * does not clamp maxPlayers or open the explanatory dialog. Use this to
   * surface the "full" state up front (e.g. a banner in the register modal);
   * use seatAvailable() on the commit path, where the dialog/clamp belong.
   */
  hasSeatAvailable(member?: any): boolean {
    let max: number = this.latestTourn.params.maxPlayers;
    const limit: number | null = this.clubServ.getPlayerLimit();
    if (limit && (!max || max > limit)) max = limit;
    let players: number = this.getPlayersCount();
    if (
      this.latestTourn.params.tournamentType == "reentry_tournament" &&
      this.latestLive.reentries
    )
      players -= this.latestLive.reentries;
    if (
      member &&
      this.latestTourn.players &&
      this.latestTourn.players[member.uid]
    )
      players--;
    if (max && players >= max) return false;
    if (!this.latestLive.seated) return true;
    for (const t in this.latestTourn.tables) {
      if (this.openSeatCount(this.latestTourn.tables[t]) > 0) return true;
    }
    return false;
  }
  // Empty seats on a table that auto-seating may actually use (avail minus
  // locked). Broken tables have no avail, so they naturally count 0.
  private openSeatCount(table: any): number {
    if (!table || !Array.isArray(table.avail)) return 0;
    const locked: number[] = Array.isArray(table.locked) ? table.locked : [];
    return table.avail.filter((s: number) => locked.indexOf(s) === -1).length;
  }
  seatAvailable(member?: any) {
    let available: boolean;
    let max: number = this.latestTourn.params.maxPlayers;
    let limit: number | null = this.clubServ.getPlayerLimit();
    if (limit && (!max || max > limit)) {
      this.changeParam("maxPlayers", limit);
      max = limit;
    }
    let players: number = this.getPlayersCount();
    if (
      this.latestTourn.params.tournamentType == "reentry_tournament" &&
      this.latestLive.reentries
    )
      players -= this.latestLive.reentries;
    if (
      member &&
      this.latestTourn.players &&
      this.latestTourn.players[member.uid]
    )
      players--;
    let allSeatsLocked = false;
    if (max && players >= max) {
      available = false;
    } else if (!this.latestLive.seated) available = true;
    else {
      available = false;
      let rawAvail = false;
      for (var t in this.latestTourn.tables) {
        const table = this.latestTourn.tables[t];
        if (table.avail && table.avail.length && !table.broke) rawAvail = true;
        if (this.openSeatCount(table) > 0) {
          available = true;
          break;
        }
      }
      // Empty seats exist but staff locked every one of them — a different
      // message than a genuinely full room.
      allSeatsLocked = !available && rawAvail;
    }
    if (!available) {
      this.loadingRegister = false;
      if (limit && players >= limit) this.dialog.open(MaxPlayersComponent);
      else if (max && players >= max)
        this.translate.get(["MAX_PLAYERS", "CLOSE"]).subscribe((res: any) => {
          this.dialog.open(AlertDialogComponent, {
            data: {
              message: res["MAX_PLAYERS"] + ": " + max,
              closeButton: res["CLOSE"],
            },
            panelClass: "alert-dialog-container",
          });
        });
      else if (allSeatsLocked) this.showAllSeatsLockedAlert();
      else
        this.translate.get(["NO_SEATS", "CLOSE"]).subscribe((res: any) => {
          this.dialog.open(AlertDialogComponent, {
            data: {
              message: res["NO_SEATS"],
              closeButton: res["CLOSE"],
            },
            panelClass: "alert-dialog-container",
          });
        });
    }
    return available;
  }
  // Every empty seat in the room is locked — register/balance/break can't
  // auto-seat until staff unlock one. Server ops return "ALL_SEATS_LOCKED".
  showAllSeatsLockedAlert() {
    this.translate.get(["ALL_SEATS_LOCKED", "CLOSE"]).subscribe((res: any) => {
      this.dialog.open(AlertDialogComponent, {
        data: {
          message: res["ALL_SEATS_LOCKED"],
          closeButton: res["CLOSE"],
        },
        panelClass: "alert-dialog-container",
      });
    });
  }
  // Resolves false when the op was rejected (or skipped by the re-entry
  // guard) so the caller can close the optimistically-opened moves dialog.
  balanceTables(): Promise<boolean> {
    // Re-entry guard: same rationale as breakTable() — don't queue a second
    // op while one is already in flight.
    if (this.loadingPlayerMoves) return Promise.resolve(false);
    // Sync prelude: clear stale state and notify the clock overlay so any
    // dialog opened in parallel with this call sees a clean slate.
    this.tableBreaking = null;
    this.initPlayerMoves();
    this.clockOverlay.showTableBalance();
    this.loadingPlayerMoves = true;
    return this.api
      .balanceTables(this.tournID)
      .then((result: any) => {
        const data = result && result.data;
        if (!data || !data.success) {
          console.log("balanceTables failed:", data);
          if (data === "ALL_SEATS_LOCKED") this.showAllSeatsLockedAlert();
          return false;
        }
        // Translate server-returned moves into the shape the UI dialog expects.
        for (const m of data.playerMoves || []) {
          if (m.unableToBalance) {
            this.playerMoves.push({ unableToBalance: true });
          } else {
            this.playerMoves.push({
              dispName: m.dispName,
              imgURL: this.clubServ.getMemberImage(m.uid),
              from: m.from,
              to: m.to,
            });
          }
        }
        this.balanceTableNames();
        this.clockOverlay.updatePlayerMoves(this.playerMoves);
        this.checkBalance();
        return true;
      })
      .finally(() => {
        this.loadingPlayerMoves = false;
      });
  }
  initPlayerMoves(table?: number) {
    if (table !== undefined) this.tableBreaking = table;
    // Mutate in place so dialogs holding a reference to playerMoves see updates
    this.playerMoves.length = 0;
  }
  // Move rows and the breaking-table banner carry DISPLAY values (custom name
  // or the global number) — resolved once here so every dialog/overlay that
  // renders them stays a dumb template.
  breakTableNames() {
    const n = Number(this.tableBreaking);
    if (n > 0) this.tableBreaking = this.tableNumberDisplay(n);
  }
  balanceTableNames() {
    for (let i = 0; i < this.playerMoves.length; i++) {
      // Skip special objects like unableToBalance that don't have from/to properties
      if (this.playerMoves[i].unableToBalance) continue;
      const from = this.playerMoves[i].from;
      const to = this.playerMoves[i].to;
      if (from && Number(from.table) > 0)
        from.table = this.tableNumberDisplay(Number(from.table));
      if (to && Number(to.table) > 0)
        to.table = this.tableNumberDisplay(Number(to.table));
    }
  }
  // Resolves false when the op was rejected (or skipped by the re-entry
  // guard) so the caller can close the optimistically-opened moves dialog.
  breakTable(tableNumber?: number): Promise<boolean> {
    // Re-entry guard: if a break/balance is already in flight, ignore the
    // call. Without this, a user double-tapping the Break Table button (or
    // tapping it while a snackbar action is also firing) would queue a
    // second server-side break against state that's already been broken
    // once — leaving players stranded with table=null/seat=null.
    if (this.loadingPlayerMoves) return Promise.resolve(false);
    // Clear stale state synchronously so any dialog/overlay opened in
    // parallel with this call doesn't show leftovers from a previous op
    // (e.g. tableBreaking=-1 from a prior balance, or a stale moves list).
    this.tableBreaking = null;
    this.initPlayerMoves();
    this.loadingPlayerMoves = true;
    return this.api
      .breakTable(this.tournID, tableNumber)
      .then((result: any) => {
        const data = result && result.data;
        if (!data || !data.success) {
          console.log("breakTable failed:", data);
          if (data === "ALL_SEATS_LOCKED") this.showAllSeatsLockedAlert();
          if (data === "INSUFFICIENT_CAPACITY") {
            this.translate
              .get("NEED_TABLE_BREAK_INSUFFICIENT_CAPACITY")
              .subscribe((msg: string) => {
                this.snackBar.open(msg, undefined, { duration: 4000 });
              });
          }
          return false;
        }
        this.initPlayerMoves(data.brokenTableNumber);
        this.translate.get(["BREAKING_TABLE"]).subscribe((res: any) => {
          const myTable = this.tableNumberDisplay(
            Number(data.brokenTableNumber),
          );
          let breakMessage = res["BREAKING_TABLE"] + ": " + myTable;
          this.snackBar.open(breakMessage, undefined, { duration: 3000 });
        });
        for (const m of data.playerMoves || []) {
          this.playerMoves.push({
            dispName: m.dispName,
            imgURL: this.clubServ.getMemberImage(m.uid),
            from: m.from,
            to: m.to,
            unableToPlace: !!m.unableToPlace,
          });
        }
        this.breakTableNames();
        this.clockOverlay.showTableBreak(this.tableBreaking);
        this.clockOverlay.updatePlayerMoves(this.playerMoves);
        this.checkBalance();
        return true;
      })
      .finally(() => {
        this.loadingPlayerMoves = false;
      });
  }
  register(member: any, regParams?: any) {
    this.lastActionIsRegister = true;
    if (!member.uid) member.manual = true;

    if (member.addToClub) {
      member.uid = this.clubServ.joinNew(member.memberName, {
        firstName: member.firstName,
        lastName: member.lastName,
        email: member.email,
      });
      if (!member.uid) {
        this.translate
          .get(["CLUB_MEMBER_ALREADY_EXISTS", "CLOSE"])
          .subscribe((res: any) => {
            this.dialog.open(AlertDialogComponent, {
              data: {
                message: res["CLUB_MEMBER_ALREADY_EXISTS"],
                closeButton: res["CLOSE"],
              },
              panelClass: "alert-dialog-container",
            });
          });
        this.loadingRegister = false;
        this.clubServ.loadingID = "";
        return;
      }
      if (member.img64) {
        let th: any = this;
        this.storageServ
          .uploadDataUrl(
            `clubs/${this.clubServ.clubID}/members/${member.uid}`,
            member.img64,
          )
          .then((url) => {
            let update: any = {};
            update["members/" + member.uid + "/imgURL"] = url;
            th.clubServ.clubAF.update(update);
          });
      }
    }
    if (!member.uid) {
      member.guest = true;
      let newRef = this.nodePush("tournaments/" + this.tournID + "/players");
      member.uid = newRef.key;
    }

    const registerPayload: any = {
      memberName: member.memberName,
      manual: !!member.manual,
      guest: !!member.guest,
      undoAction: !!(regParams && regParams.undoAction),
    };
    // Seating is handled server-side in tournamentRegister. Pass the manual
    // coordinates (1-indexed) when the caller wants a specific seat — today
    // this is only the undo-unregister path restoring the freed seat.
    if (regParams && regParams.table && regParams.seat) {
      registerPayload.manualTable = regParams.table;
      registerPayload.manualSeat = regParams.seat;
    }
    // Forward undo chip state so re-register via undo restores prior
    // stack/rebuys/addon/bonusChips/kos (rather than resetting to a fresh buyin),
    // and reentry bullet slot info so the bullet lands at its original uid.
    if (regParams && regParams.undoAction) {
      if (typeof regParams.undoStack === "number")
        registerPayload.undoStack = regParams.undoStack;
      if (regParams.undoRebuys)
        registerPayload.undoRebuys = regParams.undoRebuys;
      if (regParams.undoAddon) registerPayload.undoAddon = regParams.undoAddon;
      if (regParams.undoBonusChips)
        registerPayload.undoBonusChips = regParams.undoBonusChips;
      if (regParams.undoKos) registerPayload.undoKos = regParams.undoKos;
      if (regParams.undoBaseuid)
        registerPayload.undoBaseuid = regParams.undoBaseuid;
      if (regParams.undoBullet)
        registerPayload.undoBullet = regParams.undoBullet;
    }

    this.api
      .tournamentRegister(this.tournID, member.uid, registerPayload)
      .then((result: any) => {
        const data = result && result.data;
        if (!data || !data.success) {
          console.log("tournamentRegister failed:", data);
          // Belt and braces: seatAvailable() pre-checks locks client-side, but
          // another device may have locked the last open seat since.
          if (data === "ALL_SEATS_LOCKED") this.showAllSeatsLockedAlert();
          else if (data === "HEADSUP_BRACKET_FULL")
            this.translate
              .get(["HEADSUP_BRACKET_FULL", "CLOSE"])
              .subscribe((res: any) => {
                this.dialog.open(AlertDialogComponent, {
                  data: {
                    message: res["HEADSUP_BRACKET_FULL"],
                    closeButton: res["CLOSE"],
                  },
                  panelClass: "alert-dialog-container",
                });
              });
          this.loadingRegister = false;
          this.clubServ.loadingID = "";
          return;
        }
        const regPlayer = data.regPlayer;

        if (!regParams || !regParams.undoAction) {
          this.api.sendEvent(
            "EVT_PLAYER_REGISTERED",
            this.profServ.latestProfile,
          );
        }

        this.loadingRegister = false;
        this.clubServ.loadingID = "";

        if (regPlayer && regPlayer.table && regPlayer.seat) {
          this.translate
            .get(["BEEN_SEATED", "CLOSE", "SEAT", "TABLE"])
            .subscribe((res: any) => {
              let message =
                regPlayer.dispName +
                ":" +
                " (" +
                res["TABLE"] +
                ":" +
                this.tableNumberDisplay(Number(regPlayer.table)) +
                " " +
                res["SEAT"] +
                ":" +
                regPlayer.seat +
                ")";
              this.snackBar.open(message, res["CLOSE"], {
                duration: 10000,
                politeness: "assertive",
              });
            });
        }
      });
  }
  // Thin wrapper around the server-side movePlayer Cloud Function. Callers
  // pass 0-indexed manTable/manSeat (legacy client convention); we convert
  // to 1-indexed for the API. undoMan=true => write a MANUAL_SEAT_CHANGE
  // log entry (manual drag-drop); undoMan=false/undefined => skip the log
  // (internal reseats like undo-elimination).
  seatPlayer(
    player: any,
    manTable?: number,
    manSeat?: number,
    undoMan?: boolean,
  ) {
    if (!player || !player.uid) {
      this.api.appLog(
        "tournament.service",
        Date.now(),
        "seatPlayer",
        "missing player.uid",
        { tournID: this.tournID, player: player },
      );
      return;
    }
    this.loadingMovePlayer = true;
    const oneIndexedTable =
      manTable !== undefined && manTable !== null ? manTable + 1 : undefined;
    const oneIndexedSeat =
      manSeat !== undefined && manSeat !== null ? manSeat + 1 : undefined;
    this.api
      .movePlayer(
        this.tournID,
        player.uid,
        oneIndexedTable,
        oneIndexedSeat,
        !undoMan,
      )
      .then((result: any) => {
        this.loadingMovePlayer = false;
        const data = result && result.data;
        if (!data || !data.success) {
          console.log("movePlayer failed:", data);
          if (data === "ALL_SEATS_LOCKED") this.showAllSeatsLockedAlert();
          return;
        }
        this.checkBalance();
      });
  }

  drawSeats() {
    this.loadingDrawSeats = true;
    // Capture before the call: the draw clears needFinalTableDraw and stopping
    // the clock flips running. A final-table draw collapses to a single table
    // (needFinalTableDraw); a plain multi-table re-draw must not pause/notify.
    const isFinalTableDraw = !!(
      this.latestLive && this.latestLive.needFinalTableDraw
    );
    const wasRunning = !!(this.latestLive && this.latestLive.running);
    // If there are no tables yet, the DRAW_SEATS undo can't restore them —
    // mark existing REGISTER undos as non-independent (preserves prior
    // client behavior).
    if (!this.latestTourn.tables) this.changeRegisterUndos(false);
    this.api.drawSeats(this.tournID).then((result: any) => {
      this.loadingDrawSeats = false;
      const data = result && result.data;
      if (!data || !data.success) {
        console.log("drawSeats failed:", data);
        return;
      }
      this.mainIndex = 3;
      this.api.sendEvent("EVT_SEATS_DRAWN", this.profServ.latestProfile);
      this.clockOverlay.showSeatDraw();
      // The confirmation modal is what dismisses the full-screen seat-display
      // overlay, so it must show for every seat draw. A final-table draw while
      // the clock is running also pauses the clock (as if Pause was pressed) so
      // players can take their seats, with the modal body explaining why.
      // Synchronized split children never pause themselves — their clock
      // mirrors the parent's, and a solo pause would drift out of lockstep.
      const pauseForFinalTable =
        isFinalTableDraw && wasRunning && !this.isSyncedSplitChild;
      if (pauseForFinalTable) this.stopTimer();
      this.translate
        .get(["SEAT_DRAW_COMPLETE", "DISMISS", "CLOCK_PAUSED_FINAL_TABLE"])
        .subscribe((res: any) => {
          const seatDrawComplete =
            res["SEAT_DRAW_COMPLETE"] || "Seat draw complete";
          this.dialog
            .open(AlertDialogComponent, {
              data: {
                title: pauseForFinalTable ? seatDrawComplete : undefined,
                message: pauseForFinalTable
                  ? res["CLOCK_PAUSED_FINAL_TABLE"]
                  : seatDrawComplete,
                closeButton: res["DISMISS"] || "Dismiss",
              },
              panelClass: "alert-dialog-container",
            })
            .afterClosed()
            .subscribe(() => {
              this.clockOverlay.dismiss();
              // Land on the clock tab as a reminder to (re)start the clock.
              this.mainIndex = 0;
            });
        });
    });
  }
  advanceShootoutRound() {
    const nextRound = this.shootoutRound + 1;
    this.translate
      .get(
        [
          "SHOOTOUT_ADVANCE_CONFIRM_TITLE",
          "SHOOTOUT_ADVANCE_CONFIRM_BODY",
          "SHOOTOUT_ADVANCE",
          "CANCEL",
        ],
        { round: nextRound },
      )
      .subscribe((res: any) => {
        this.dialog
          .open(ConfirmDialogComponent, {
            data: {
              title: res["SHOOTOUT_ADVANCE_CONFIRM_TITLE"],
              message: res["SHOOTOUT_ADVANCE_CONFIRM_BODY"],
              acceptButton: res["SHOOTOUT_ADVANCE"],
              cancelButton: res["CANCEL"],
            },
            panelClass: "confirm-dialog-container",
          })
          .afterClosed()
          .subscribe((accept: boolean) => {
            if (accept) this.executeShootoutAdvance(nextRound);
          });
      });
  }
  private executeShootoutAdvance(nextRound: number) {
    this.loadingDrawSeats = true;
    const wasRunning = !!(this.latestLive && this.latestLive.running);
    this.api.advanceShootoutRound(this.tournID).then((result: any) => {
      this.loadingDrawSeats = false;
      const data = result && result.data;
      if (!data || !data.success) {
        console.log("advanceShootoutRound failed:", data);
        // Structured reject (e.g. SHOOTOUT_ROUND_INCOMPLETE when another
        // device already advanced) → translated toast.
        const code = typeof data === "string" ? data : "SHOOTOUT_ERROR";
        this.translate.get(code).subscribe((msg: string) => {
          this.snackBar.open(msg, undefined, { duration: 4000 });
        });
        return;
      }
      this.mainIndex = 3;
      this.api.sendEvent("EVT_SHOOTOUT_ROUND", this.profServ.latestProfile);
      this.clockOverlay.showSeatDraw();
      // Advancing always pauses a running clock — players must find their
      // new seats (same reasoning as the final-table draw pause).
      if (wasRunning) this.stopTimer();
      this.translate
        .get(["SHOOTOUT_ADVANCE_COMPLETE", "DISMISS"], { round: nextRound })
        .subscribe((res: any) => {
          this.dialog
            .open(AlertDialogComponent, {
              data: {
                message: res["SHOOTOUT_ADVANCE_COMPLETE"],
                closeButton: res["DISMISS"] || "Dismiss",
              },
              panelClass: "alert-dialog-container",
            })
            .afterClosed()
            .subscribe(() => {
              this.clockOverlay.dismiss();
              // Land on the clock tab as a reminder to (re)start the clock.
              this.mainIndex = 0;
            });
        });
    });
  }
  stacksUpdated() {
    this.liveAF
      .update({
        stacksUpdated: true,
      })
      .then(() => {
        // this.recycleInfo(); // Now handled by InfoBoxService
      });
  }
  // Manual stack edit (player-stack dialog). Routes to the player's child
  // tournament when edited from a Split parent's roster, flagging that child's
  // live.stacksUpdated so ITS clock leaderboard switches to tracked stacks.
  setPlayerStack(player: any, stack: number) {
    if (!player?.uid) return;
    const tid = this.opTournID(player.uid, player._childTournID);
    if (tid === this.tournID) {
      if (!this.latestLive.stacksUpdated) this.stacksUpdated();
      this.playersAF.update(player.uid, { stack, manStack: true });
    } else {
      this.rootUpdate({
        [`tournaments/${tid}/players/${player.uid}/stack`]: stack,
        [`tournaments/${tid}/players/${player.uid}/manStack`]: true,
        [`tournaments/${tid}/live/stacksUpdated`]: true,
      });
    }
  }

  // Info box methods have been moved to InfoBoxService

  initClock() {
    this.clockDelta((delta: number) => {
      this.deltaSig.set(delta);
    });
  }
  clockDelta(callback: any) {
    var init: any;
    const deltaRef = this.nodePush("deltas");
    runInInjectionContext(this.injector, () =>
      onValue(deltaRef, function (snapshot) {
        if (snapshot.val() && init) {
          var end = Date.now();
          var rt = end - start;
          var server = snapshot.val();
          var delta = end - server - rt / 2;
          callback(delta);
        }
        if (!init) init = true;
      }),
    );
    var start = Date.now();
    runInInjectionContext(this.injector, () =>
      set(deltaRef, serverTimestamp()),
    );
  }
  // The 500ms tick driver (tournament.component setInterval, clock tab only).
  // Just advances the tick counter so the `clock` computed re-derives. The
  // server-clock skew correction (client ahead of server → elapsed < 0) stays
  // here in the imperative driver since the computed must be pure.
  tick() {
    this.checkRotate();
    const live = this.latestLive;
    if (live && live.running && live.started) {
      let elapsed = Math.floor(
        (Date.now() - live.started - this.deltaSig()) / 1000,
      );
      while (elapsed < 0) {
        this.deltaSig.update((d) => d + elapsed * 100);
        elapsed = Math.floor(
          (Date.now() - live.started - this.deltaSig()) / 1000,
        );
      }
    }
    this.tickCounter.update((v) => v + 1);
  }

  // Pure clock derivation — was tick()+setClock()+setBlinds()+findNextBreak(),
  // with every `this._clock.X =` replaced by a returned field and all side-effects
  // (sounds, tracking, real(), the auto-stop writeback) lifted into the clock
  // effect in the constructor. Reactive deps: tickCounter, deltaSig, latestLive,
  // structure, players (ante-halving). Returns the stable empty object until a
  // tournament+structure are loaded.
  private computeClock(): any {
    return this.computeClockFor(
      this.latestLive,
      this.getPlayersCount() - (this.latestLive?.out || 0),
    );
  }
  // The clock math for an arbitrary live node (the structure is shared —
  // split children carry verbatim copies of the parent's levels).
  // remainingCount feeds the <6-players ante-halving rule.
  private computeClockFor(live: any, remainingCount: number): any {
    this.tickCounter();
    const delta = this.deltaSig();
    const structure = this.structure;
    if (!live || !structure || !structure.length) return this.emptyClock;

    // seconds = server seconds + client elapsed while running (else just server).
    let seconds: number;
    if (live.running && live.started) {
      const elapsed = Math.max(
        0,
        Math.floor((Date.now() - live.started - delta) / 1000),
      );
      seconds = live.seconds + elapsed;
    } else {
      seconds = live.seconds;
    }
    if (seconds == undefined) return this.emptyClock;

    // Walk the levels; clamp + flag overrun past the final level.
    let lvl = 0;
    let overrun = false;
    let sec = seconds;
    let level = structure[lvl];
    while (sec >= level.levelTime * 60) {
      if (lvl === structure.length - 1) {
        // Past the end of the structure: clamp ONCE to the last level's end
        // (seconds = total structure time, 0:00 on the level). The old loop
        // kept subtracting on every extra iteration, so a tournament left
        // running for hours drove `seconds` hugely negative — garbage clock,
        // level 1 re-highlighted, and a negative seconds write on auto-stop.
        overrun = true;
        seconds -= sec - level.levelTime * 60;
        sec = level.levelTime * 60;
        break;
      }
      sec -= level.levelTime * 60;
      lvl++;
      level = structure[lvl];
    }
    const atEnd = lvl === structure.length - 1;

    // Blinds, incl. the < 6-player ante halving (was setBlinds()).
    let blindLevel = level;
    if (
      this.latestTourn &&
      this.latestTourn.params.anteType != "standard" &&
      this.latestTourn.params.antes_half &&
      remainingCount < 6
    ) {
      blindLevel = this.util.copyObject(level);
      blindLevel.ante = math.round(blindLevel.ante / 2, 2);
    }
    const kLevel = this.util.kBlinds(blindLevel);

    // Next break (was findNextBreak()).
    let nb = 999999;
    for (let i = 0; i < structure.length; i++)
      if (structure[i].breakStart > seconds && structure[i].breakStart < nb)
        nb = structure[i].breakStart;
    const nextBreakBase = nb == 999999 ? 0 : nb;

    let timeSec = level.levelTime * 60 - sec;
    if (overrun) timeSec = 0;
    const nextBreakSec = nextBreakBase - seconds;

    return {
      seconds,
      level: lvl,
      levelTime: level.levelTime,
      runningTime: this.util.timeText(seconds),
      timeSec,
      time: this.util.timeText(timeSec),
      sb: kLevel.sb,
      bb: kLevel.bb,
      rawBb: blindLevel.bb,
      ante: kLevel.ante,
      breakStart: blindLevel.breakStart,
      levelName: blindLevel.levelName,
      nextBreakSec,
      nextBreak: nextBreakSec < 0 ? "--:--" : this.util.timeText(nextBreakSec),
      overrun,
      atEnd,
    };
  }
  real(locationData: any) {
    if (
      this.latestTourn &&
      this.latestTourn.params &&
      !this.latestTourn.params.real
    ) {
      let th = this;
      this.nodeTransaction(
        "tournaments/" + this.tournID + "/params/real",
        function (real) {
          if (!real) {
            return true;
          } else return;
        },
      ).then((result: any) => {
        if (result && result.committed) {
          th.clubServ.updateTournParams(
            th.tournID,
            { real: true },
            th.latestTourn?.clubID,
          );
          th.realConfirm(locationData);
        }
      });
    }
  }
  statManPlayers() {
    if (
      !this.latestTourn.params.actPlayers ||
      this.latestTourn.params.expPlayers > this.latestTourn.params.actPlayers
    )
      return this.latestTourn.params.expPlayers;
    else return this.latestTourn.params.actPlayers;
  }
  realConfirm(locData: any) {
    let statObj: any = {
      timestamp: serverTimestamp(),
      ip_address: locData.traits.ip_address,
      startingStack: parseInt(this.latestTourn.params.startingStack),
      rebuyTournament:
        this.latestTourn.params.tournamentType == "rebuy_tournament",
      tournamentType: this.latestTourn.params.tournamentType,
      level_time: this.latestTourn.params.levelTime,
      running_time: this.latestTourn.params.duration,
      chip1: this.latestTourn.params.chipset[0] ?? null,
      chip2: this.latestTourn.params.chipset[1] ?? null,
      chip3: this.latestTourn.params.chipset[2] ?? null,
      chip4: this.latestTourn.params.chipset[3] ?? null,
      chip5: this.latestTourn.params.chipset[4] ?? null,
      locationData: locData,
      tournID: this.tournID,
    };
    if (this.profServ.uid) statObj.uid = this.profServ.uid;
    if (this.profServ.latestSubscription) statObj.subscriber = true;
    else statObj.subscriber = false;
    if (isRebuyCapable(this.latestTourn.params.tournamentType)) {
      statObj.rebuys = this.latestTourn.params.rebuys || 0;
      statObj.addons = this.latestTourn.params.addons || 0;
    }
    if (this.latestTourn.params.tournamentType == "reentry_tournament")
      statObj.reentries = this.latestTourn.params.reentries || 0;

    if (this.latestTourn.params.manageRegistrations) {
      if (
        this.latestTourn.players &&
        Object.keys(this.latestTourn.players).length > 1
      )
        statObj.players = Object.keys(this.latestTourn.players).length;
      else statObj.players = this.statManPlayers();
    } else statObj.players = this.statManPlayers();

    statObj.players = parseInt(statObj.players);

    if (this.latestTourn.params.antes) statObj.antes = true;
    else statObj.antes = false;

    statObj.startTime = this.latestLive.started;
    statObj.endTime =
      this.latestLive.started +
      1000 * 60 * 60 * this.latestTourn.params.duration;
    this.nodePush("tourney_stats", statObj);
    var dayDate = this.util.shedTime(statObj.startTime);

    const incCount = (count: any) => (count ? count + 1 : 1);
    const addPlayers = (count: any) =>
      count ? parseInt(count) + statObj.players : statObj.players;

    this.nodeTransaction("globals/tournamentCount", incCount);
    this.nodeTransaction("globals/tournaments/" + dayDate, incCount);
    this.nodeTransaction("globals/audience/" + dayDate, addPlayers);
    this.nodeTransaction(
      "globals/countryTournaments/" + locData.country + "/" + dayDate,
      incCount,
    );
    this.nodeTransaction(
      "globals/countryAudience/" + locData.country + "/" + dayDate,
      addPlayers,
    );

    // partitioned data
    let partPrefix = statObj.subscriber ? "subscribers" : "free";
    this.nodeTransaction(
      "globals/" + partPrefix + "/tournamentCount",
      incCount,
    );
    this.nodeTransaction(
      "globals/" + partPrefix + "/tournaments/" + dayDate,
      incCount,
    );
    this.nodeTransaction(
      "globals/" + partPrefix + "/audience/" + dayDate,
      addPlayers,
    );
    this.nodeTransaction(
      "globals/" +
        partPrefix +
        "/countryTournaments/" +
        locData.country +
        "/" +
        dayDate,
      incCount,
    );
    this.nodeTransaction(
      "globals/" +
        partPrefix +
        "/countryAudience/" +
        locData.country +
        "/" +
        dayDate,
      addPlayers,
    );
  }
  checkRotate() {
    // TODO
  }
  // Jump one level forward/back: write the new position to Firebase (setTimer/
  // stopTimer); the `clock` computed re-derives level + blinds from the round-trip
  // — no optimistic _clock mutation needed.
  nextLevel() {
    const target = (this.clockSig()?.level ?? 0) + 1;
    let newSec = 0;
    for (let i = 0; i < target; i++) newSec += this.structure[i].levelTime * 60;
    this.setTimer(newSec);
    this.tracking.track("Next Level", this.latestLive);
    this.api.sendEvent("EVT_NEXT_LEVEL", this.profServ.latestProfile);
  }
  prevLevel() {
    const cur = this.clockSig()?.level ?? 0;
    if (cur <= 3 && this.latestTourn.params.real) {
      this.changeParam("real", false);
    }
    if (cur < 2) {
      this.stopTimer(0);
    } else {
      const target = cur - 1;
      let newSec = 0;
      for (let i = 0; i < target; i++)
        newSec += this.structure[i].levelTime * 60;
      this.stopTimer(newSec);
    }
    this.tracking.track("Prev Level", this.latestLive);
  }
  validTournament() {
    let player_limit: number | null = this.clubServ.getPlayerLimit();
    let registeredPlayers: number = 0;
    if (player_limit) {
      if (this.latestTourn.params.manageRegistrations)
        registeredPlayers =
          this.latestTourn.playerCount - (this.latestLive.reentries || 0);
      else registeredPlayers = this.latestTourn.params.actPlayers;
      if (registeredPlayers <= player_limit) return true;
      else return false;
    } else return true;
  }
  notValidMessage() {
    this.dialog.open(MaxPlayersComponent);
  }
  // Fan a clock write out to a Split parent + all its unfinished children in
  // one atomic root update, so every clock stays in lockstep (the level is
  // derived from live.seconds/started + the identical per-child structure
  // copy). Children flip to "Running" on first start; the parent keeps its
  // terminal "Split" status.
  private splitClockFanout(liveData: any, startRunning = false): Promise<void> {
    const splitInto = this.latestTourn?.splitInto || {};
    const updates: any = {};
    const targets = [this.tournID];
    for (const childID in splitInto) {
      if (splitInto[childID]?.tournStatus !== "Finished") targets.push(childID);
    }
    for (const tid of targets) {
      for (const key in liveData) {
        updates[`tournaments/${tid}/live/${key}`] = liveData[key];
      }
    }
    if (startRunning) {
      const children = this._splitChildren();
      for (const tid of targets) {
        if (tid === this.tournID) continue;
        const rec = children.find((c: any) => c.tournID === tid);
        if (!rec || rec.tournStatus !== "Running") {
          updates[`tournaments/${tid}/tournStatus`] = "Running";
        }
      }
    }
    return this.rootUpdate(updates);
  }
  startTimer() {
    if (this.validTournament()) {
      if (this.isSyncedSplitParent) {
        // Parent keeps its terminal "Split" status; "first start" is when a
        // child hasn't flipped to Running yet.
        const firstStart = this._splitChildren().some(
          (c: any) => c.tournStatus !== "Running",
        );
        this.splitClockFanout({ running: true, started: serverTimestamp() }, true);
        if (firstStart) {
          this.api.sendEvent(
            "EVT_TOURNAMENT_STARTED",
            this.profServ.latestProfile,
          );
        }
        return;
      }
      let update: any = {};
      update["live/running"] = true;
      update["live/started"] = serverTimestamp();
      const status = this.clockStatusChange({ running: true });
      if (status) update["tournStatus"] = status;
      if (status === "Running") {
        this.api.sendEvent(
          "EVT_TOURNAMENT_STARTED",
          this.profServ.latestProfile,
        );
      }
      this.tournamentAF.update(update);
    } else this.notValidMessage();
  }
  // Status is derived from the state the clock write will produce (mirror of
  // the server rule, see tourn-status.ts). Returns the new status when it
  // differs from the current one, else null. Stopping the clock at 0 with no
  // eliminations drops the tournament back to Seating/Scheduled.
  private clockStatusChange(liveNext: any): string | null {
    const t = this.latestTourn;
    if (!t) return null;
    const next = deriveTournStatus({
      ...t,
      live: { ...(t.live || {}), ...liveNext },
    });
    return next && next !== t.tournStatus ? next : null;
  }
  // Clock write + derived status in one multi-path update on the tournament
  // node (the status lives beside `live`, not under it).
  private writeClock(live: any) {
    const update: any = {};
    for (const key in live) update[`live/${key}`] = live[key];
    const status = this.clockStatusChange(live);
    if (status) update["tournStatus"] = status;
    return this.tournamentAF.update(update);
  }
  stopTimer(seconds?: number) {
    const newSeconds =
      seconds! >= 0
        ? seconds
        : (this.clockSig()?.seconds ?? this.latestLive?.seconds ?? 0);
    this.latestLive.running = false;
    this.latestLive.seconds = newSeconds;
    let update: any = {};
    update["running"] = false;
    update["seconds"] = newSeconds;
    if (this.isSyncedSplitParent) {
      this.splitClockFanout(update);
      return;
    }
    this.writeClock(update);
  }
  toggleHandForHand() {
    const newState = !this.latestLive.hand_for_hand;

    if (newState && this.latestLive.running) {
      // Stop timer when enabling hand-for-hand
      this.stopTimer();
    } else if (!newState && !this.latestLive.running) {
      // Start timer when disabling hand-for-hand
      this.startTimer();
    }

    // Update hand_for_hand state
    this.latestLive.hand_for_hand = newState;
    if (this.isSyncedSplitParent) {
      this.splitClockFanout({ hand_for_hand: newState });
      return;
    }
    this.liveAF.update({ hand_for_hand: newState });
  }
  minus2Min() {
    if (this.latestLive && this.latestLive.hand_for_hand) {
      const newSeconds = this.latestLive.seconds + 120;
      this.setTimer(newSeconds);
    }
  }
  setTimer(seconds: number) {
    let update: any = {};
    update["started"] = serverTimestamp();
    update["seconds"] = seconds;
    if (this.isSyncedSplitParent) {
      this.splitClockFanout(update);
      return;
    }
    this.writeClock(update);
  }
  changeClockSeconds(seconds: number) {
    const lvl = this.clockSig()?.level ?? 0;
    seconds = this.structure[lvl].levelTime * 60 - seconds;
    for (var i = 0; i < lvl; i++) seconds += this.structure[i].levelTime * 60;
    this.setTimer(seconds);
  }
  addTable() {
    this.loadingAddTable = true;
    // Server-authoritative: the addTable Cloud Function appends the empty table,
    // recomputes the seating flags, and writes the ADD_TABLE undo-log entry —
    // matching every other seating mutation (no direct client write).
    this.api.addTable(this.tournID).finally(() => {
      this.loadingAddTable = false;
    });
    this.api.sendEvent("EVT_ADD_TABLE", this.profServ.latestProfile);
  }
  updateSeatingParams(params: {
    extraSeats: number;
    perTable: number;
    numberPlayersFinalTable: number;
  }) {
    // Server-authoritative: validates a perTable reduction against occupied
    // seats, resizes the table seat arrays, and recomputes the seating flags
    // (needTableBreak/needTableBalance/needFinalTableDraw) under the new
    // numbers — a plain params write would leave those flags stale.
    return this.api.updateSeatingParams(this.tournID, params);
  }
  changeRegisterUndos(independent: boolean) {
    // Logs live at /logs/tournaments/{tournID}, not /tournaments/{tournID}/log.
    // latestLog is already populated from the correct path via snapshotChanges.
    if (!this.latestLog || !this.latestLog.length) return;
    const update: any = {};
    for (const entry of this.latestLog) {
      if (entry && entry.type === "REGISTER") {
        update[`${entry.key}/undos/dependent`] = false;
        update[`${entry.key}/undos/independent`] = independent;
      }
    }
    if (Object.keys(update).length) {
      this.nodeUpdate("logs/tournaments/" + this.tournID, update);
    }
  }
  logUndo(entry: any) {
    this.tracking.track("UNDO" + entry.type);

    // Special handling for MERGE_TOURNAMENT - requires confirmation.
    if (entry.type == "MERGE_TOURNAMENT") {
      this.undoMergeTournament(entry);
      return;
    }

    // Special handling for SPLIT_TOURNAMENT — confirmation, then the
    // server-side undo (which refuses once any child has activity).
    if (entry.type == "SPLIT_TOURNAMENT") {
      this.undoSplitTournament(entry);
      return;
    }

    // SWAP_FINISH: the server refuses once either player changed since the
    // swap — so only step the undo index back when it actually went through.
    if (entry.type == "SWAP_FINISH") {
      this.loadingUndo = true;
      this.api
        .logUndo(this.tournID, entry.key)
        .then((result: any) => {
          const data = result && result.data;
          if (data && data.success) {
            if (entry.undoIdx) this.changeUndoIdx(-1);
            return;
          }
          console.error("undo swap returned:", data);
          const code =
            data === "STATE_CHANGED" ? "SWAP_UNDO_BLOCKED" : "GENERIC_ERROR";
          this.snackBar.open(this.translate.instant(code), undefined, {
            duration: 5000,
          });
        })
        .catch((err: any) => console.error("undo swap error:", err))
        .finally(() => (this.loadingUndo = false));
      return;
    }

    // Server-side undo dispatcher handles these atomically. Other types
    // (e.g. future additions) fall back to the client-side paths below.
    const SERVER_TYPES = new Set([
      "ELIMINATION",
      "MYSTERY_DRAW",
      "DRAW_SEATS",
      "BREAK_TABLE",
      "BALANCE_TABLES",
      "MANUAL_SEAT_CHANGE",
      "ADD_TABLE",
      "SHOOTOUT_ROUND",
      "REGISTER",
      "UNREGISTER",
      "REBUY",
      "ADDON",
      "REENTRY",
      "BONUS_CHIPS",
    ]);
    if (SERVER_TYPES.has(entry.type)) {
      if (entry.undoIdx) this.changeUndoIdx(-1);
      this.loadingUndo = true;
      this.api
        .logUndo(this.tournID, entry.key)
        .then((result: any) => {
          const data = result && result.data;
          if (!data || !data.success) {
            console.error("logUndo returned:", data);
          }
          if (this.latestTables) this.checkBalance();
        })
        .catch((err: any) => {
          console.error("logUndo error:", err);
        })
        .finally(() => {
          this.loadingUndo = false;
        });
      return;
    }
  }

  /**
   * Undo a tournament merge - shows confirmation, deletes merged tournament,
   * restores source tournaments to Running status, and navigates to lobby
   */
  undoMergeTournament(entry: any) {
    const mergedTournamentNames = entry.undoParams?.mergedTournamentNames || [];
    const mergedTournamentIds = entry.undoParams?.mergedTournamentIds || [];

    this.translate
      .get(["UNDO_MERGE_WARNING", "UNDO_MERGE_CONFIRM", "CONFIRM", "CANCEL"])
      .subscribe((res: any) => {
        const message =
          res["UNDO_MERGE_WARNING"] ||
          "This will delete the merged tournament and restore the original tournaments. Are you sure?";

        this.dialog
          .open(ConfirmDialogComponent, {
            data: {
              title: res["UNDO_MERGE_CONFIRM"] || "Undo Merge",
              message: message,
              acceptButton: res["CONFIRM"] || "Confirm",
              cancelButton: res["CANCEL"] || "Cancel",
            },
            panelClass: "confirm-dialog-container",
          })
          .afterClosed()
          .subscribe(async (accept: boolean) => {
            if (accept) {
              await this.executeUndoMerge(mergedTournamentIds, entry.key);
            }
          });
      });
  }

  /**
   * Execute the undo merge operation
   */
  private async executeUndoMerge(
    mergedTournamentIds: string[],
    logEntryKey: string,
  ) {
    this.util.loading = true;

    try {
      const currentTournID = this.tournID;
      const clubID = this.latestTourn.clubID;
      const updates: any = {};

      // 1. Restore source tournaments to Running status and restore leagues
      for (const sourceTournID of mergedTournamentIds) {
        updates["tournaments/" + sourceTournID + "/tournStatus"] = "Running";
        updates["tournaments/" + sourceTournID + "/mergedInto"] = null;

        // Re-add leagues if they were on the merged tournament
        if (this.latestTourn.leagues) {
          updates["tournaments/" + sourceTournID + "/leagues"] =
            this.latestTourn.leagues;
          updates[
            "clubs/" + clubID + "/tournaments/" + sourceTournID + "/leagues"
          ] = this.latestTourn.leagues;

          for (const leagueID in this.latestTourn.leagues) {
            updates["leagues/" + leagueID + "/tournaments/" + sourceTournID] = {
              tournID: sourceTournID,
              tournName: "", // Will need to fetch or we accept empty
            };
          }
        }
      }

      // 2. Remove merged tournament from leagues
      if (this.latestTourn.leagues) {
        for (const leagueID in this.latestTourn.leagues) {
          updates["leagues/" + leagueID + "/tournaments/" + currentTournID] =
            null;
        }
      }

      // 3. Delete the merged tournament
      updates["tournaments/" + currentTournID] = null;
      updates["clubs/" + clubID + "/tournaments/" + currentTournID] = null;
      updates["structures/" + currentTournID] = null;
      updates["payouts/" + currentTournID] = null;
      updates["logs/tournaments/" + currentTournID] = null;

      // 4. Execute all updates
      await this.rootUpdate(updates);

      // 5. Navigate to lobby
      this.util.loading = false;
      this.dialog.closeAll();
      this.routing.toLobby();

      this.tracking.track("UNDO_MERGE_TOURNAMENT");
    } catch (error) {
      this.util.loading = false;
      console.error("Error undoing merge:", error);
    }
  }
  /**
   * Undo a tournament split — confirmation, then the server-side logUndo,
   * which refuses once any child has activity and otherwise atomically
   * deletes the children and restores the parent to Scheduled.
   */
  undoSplitTournament(entry: any) {
    this.translate
      .get(["UNDO_SPLIT_WARNING", "UNDO_SPLIT_CONFIRM", "CONFIRM", "CANCEL"])
      .subscribe((res: any) => {
        this.dialog
          .open(ConfirmDialogComponent, {
            data: {
              title: res["UNDO_SPLIT_CONFIRM"],
              message: res["UNDO_SPLIT_WARNING"],
              acceptButton: res["CONFIRM"],
              cancelButton: res["CANCEL"],
            },
            panelClass: "confirm-dialog-container",
          })
          .afterClosed()
          .subscribe((accept: boolean) => {
            if (!accept) return;
            this.loadingUndo = true;
            this.api
              .logUndo(this.tournID, entry.key)
              .then((result: any) => {
                const data = result && result.data;
                if (!data || !data.success) {
                  console.error("undo split returned:", data);
                  const code = typeof data === "string" ? data : "SPLIT_ERROR";
                  this.translate.get(code).subscribe((msg: string) => {
                    this.snackBar.open(msg, undefined, { duration: 5000 });
                  });
                  return;
                }
                this.tracking.track("UNDO_SPLIT_TOURNAMENT");
              })
              .catch((err: any) => console.error("undo split error:", err))
              .finally(() => (this.loadingUndo = false));
          });
      });
  }
  // Firebase key of a local row: its original index in /payouts (recorded by
  // setLocalPayouts). Never derive it from place — a legacy table can hold a
  // row whose place doesn't match its index.
  private payoutKey(payout: any): string {
    return (payout.idx != null ? payout.idx : payout.place - 1).toString();
  }
  updateAward(val: number, payout: any) {
    let percent: number = math.round(
      (val / this.latestTourn.params.prizepool) * 100,
      2,
    );
    payout.percent = percent;
    this.payoutsAF
      .update(this.payoutKey(payout), { award: val, percent: percent })
      .then((data) => {
        this.updateAwardDeltas();
      });
  }
  updatePercent(val: number, payout: any) {
    const prizepool = this.latestTourn.params.prizepool;
    const placesPaid = this.localPayouts ? this.localPayouts.length : 0;
    let award: number = this.roundManualAward(
      (val / 100) * prizepool,
      prizepool,
      placesPaid,
    );
    payout.award = award;
    this.payoutsAF
      .update(this.payoutKey(payout), { award: award, percent: val })
      .then((data) => {
        this.updateAwardDeltas();
      });
  }
  updateAwardDeltas() {
    if (this.localPayouts) {
      let awardTot: number = 0;
      let percentTot: number = 0;
      for (var i = 0; i < this.localPayouts.length; i++) {
        let payout = this.localPayouts[i];
        awardTot += payout.award;
        percentTot += payout.percent;
      }
      this.awardDelta = math.round(
        awardTot - this.latestTourn.params.prizepool,
        2,
      );
      this.percentDelta = math.round(percentTot - 100, 2);
    }
  }
  setLocalPayouts() {
    if (this.latestPayouts && this.latestTourn && this.latestTourn.params) {
      // Tag each row with its /payouts index (the write key), then order by
      // place for display — the editor iterates this list directly.
      this.localPayouts = this.util
        .copyArray(this.latestPayouts)
        .map((p, i) => ({ ...p, idx: i }))
        .sort((a, b) => a.place - b.place);
      for (var i = 0; i < this.localPayouts.length; i++) {
        // Preserve the user's entered percentages as the source of truth.
        // Only derive from the award for legacy rows that have no stored
        // percent — re-deriving here would make rounded awards drift the
        // displayed percentages (e.g. 30 -> 30.23).
        if (this.localPayouts[i].percent == null) {
          this.localPayouts[i].percent = math.round(
            (this.localPayouts[i].award / this.latestTourn.params.prizepool) *
              100,
            2,
          );
        }
      }
      this.updateAwardDeltas();
    } else {
      this.needPayoutsInit = true;
    }
  }
  syncLocalPayoutsFromLatest() {
    // Full rebuild if not initialized or the number of paid places changed.
    if (
      !this.localPayouts ||
      !this.latestPayouts ||
      this.localPayouts.length !== this.latestPayouts.length
    ) {
      this.setLocalPayouts();
      return;
    }
    // Otherwise mutate awards/percents in place (preserve object refs so an
    // input being edited doesn't lose focus). Match by place.
    for (let i = 0; i < this.latestPayouts.length; i++) {
      const src = this.latestPayouts[i];
      const dst = this.localPayouts.find((p) => p.place === src.place);
      if (dst) {
        dst.idx = i;
        dst.award = src.award;
        dst.percent = src.percent;
      }
    }
    this.updateAwardDeltas();
  }
  // --- Mystery bounty manual prize editing ---
  // Seed the editable list from the persisted prizes, or from the generated
  // distribution if nothing has been hand-set yet.
  setLocalBountyPrizes() {
    const p = this.latestTourn?.params;
    if (!p) return;
    const src =
      p.bountyPrizes && p.bountyPrizes.length
        ? p.bountyPrizes
        : this.bountyPrizesPreview;
    this.localBountyPrizes = (src || []).map((award: number, i: number) => ({
      place: i + 1,
      award,
    }));
    this.updateBountyDelta();
  }
  updateBountyPrize(val: number, idx: number) {
    if (!this.localBountyPrizes || !this.localBountyPrizes[idx]) return;
    this.localBountyPrizes[idx].award = val;
    // Light direct write (no param cascade), like the payout editor's payoutsAF.
    const bountyPrizes = this.localBountyPrizes.map((pr) => pr.award);
    this.paramsAF.update({ bountyPrizes });
    this.clubServ.updateTournParams(
      this.tournID,
      { bountyPrizes },
      this.latestTourn?.clubID,
    );
    this.updateBountyDelta();
  }
  updateBountyDelta() {
    if (this.localBountyPrizes) {
      let tot = 0;
      for (const pr of this.localBountyPrizes) tot += pr.award || 0;
      this.bountyDelta = math.round(
        tot - (this.latestTourn?.params?.bountyPool || 0),
        2,
      );
    }
  }
  // Switching back to auto (the calculator toggle) discards the manual override
  // so the generated distribution takes over again — same as the payout editor.
  clearBountyPrizes() {
    this.paramsAF.update({ bountyPrizes: null });
    this.clubServ.updateTournParams(
      this.tournID,
      { bountyPrizes: null },
      this.latestTourn?.clubID,
    );
    this.localBountyPrizes = [];
    this.bountyDelta = 0;
  }
  recalculateLeagues() {
    if (this.latestTourn && this.latestTourn.leagues)
      for (let l in this.latestTourn.leagues)
        this.api.recalculateLeagueStandings(l);
  }
  updatePointsMultipliers(multipliers: any[]) {
    let update: any = {};
    for (let i = 0; i < multipliers.length; i++) {
      const mult = multipliers[i];
      if (!mult.multiplier) mult.multiplier = 1;
      if (mult.leagueID) {
        update["leagues/" + mult.leagueID + "/multiplier"] = mult.multiplier;
      }
    }
    this.tournamentAF.update(update).then(() => {
      this.recalculateLeagues();
    });
  }
  updateBonusPoints(
    leagueID: string,
    bonusPointsMap: { [uid: string]: number },
  ) {
    let update: any = {};
    for (let uid in bonusPointsMap) {
      const points = bonusPointsMap[uid];
      update["players/" + uid + "/points-bonus/" + leagueID] = points;
    }
    // Also remove bonus points for players set to zero (to keep DB clean)
    if (this.latestTourn && this.latestTourn.players) {
      for (let uid in this.latestTourn.players) {
        const player = this.latestTourn.players[uid];
        if (
          player["points-bonus"] &&
          player["points-bonus"][leagueID] !== undefined
        ) {
          // If player had bonus points but is not in the new map, set to null to remove
          if (bonusPointsMap[uid] === undefined) {
            update["players/" + uid + "/points-bonus/" + leagueID] = null;
          }
        }
      }
    }
    this.tournamentAF.update(update).then(() => {
      this.recalculateLeagues();
    });
  }
  addBonusChips(player: any, bonusChips: number) {
    if (player && player.uid && bonusChips) {
      this.loadingChipOp = true;
      this.api
        .tournamentBonusChips(
          this.opTournID(player.uid, player._childTournID),
          player.uid,
          bonusChips,
        )
        .then((result: any) => {
          const data = result && result.data;
          if (!data || !data.success) {
            console.error("tournamentBonusChips returned:", data);
          }
        })
        .catch((err: any) => {
          console.error("tournamentBonusChips error:", err);
        })
        .finally(() => {
          this.loadingChipOp = false;
        });
    }
  }
  // Individual bounty: set (value > 0) or clear (value <= 0) the head value on a
  // specific player. Server carves it from the prizepool into the bounty pool.
  addBounty(player: any, value: number) {
    if (!player || !player.uid) return;
    this.loadingChipOp = true;
    this.api
      .tournamentSetBounty(
        this.opTournID(player.uid, player._childTournID),
        player.uid,
        Number(value) || 0,
      )
      .then((result: any) => {
        const data = result && result.data;
        if (!data || !data.success) {
          console.error("tournamentSetBounty returned:", data);
        }
      })
      .catch((err: any) => {
        console.error("tournamentSetBounty error:", err);
      })
      .finally(() => {
        this.loadingChipOp = false;
      });
  }
  removeBounty(player: any) {
    this.addBounty(player, 0);
  }
  // Clear every individual bounty (used when switching a None tournament to a
  // mode that owns the carve). Returns the promise so the caller can sequence
  // the mode change after the clear commits.
  clearIndividualBounties(): Promise<any> {
    return this.api
      .tournamentClearBounties(this.tournID)
      .then((result: any) => {
        const data = result && result.data;
        if (!data || !data.success) {
          console.error("tournamentClearBounties returned:", data);
        }
        return data;
      })
      .catch((err: any) => {
        console.error("tournamentClearBounties error:", err);
        return null;
      });
  }
  updatePlayerCount(count: number) {
    this.nodeSet("tournaments/" + this.tournID + "/playerCount", count);
    this.nodeSet(
      "clubs/" +
        this.latestTourn.clubID +
        "/tournaments/" +
        this.tournID +
        "/playerCount",
      count,
    );
  }
  checkForGhosts() {
    if (this.latestTourn && this.latestTourn.players) {
      let update: any;
      let count = 0;
      for (let p in this.latestTourn.players) {
        if (!this.latestTourn.players[p].uid) {
          if (!update) update = {};
          update[p] = null;
        } else count++;
      }
      if (update) {
        this.nodeUpdate("tournaments/" + this.tournID + "/players", update);
        if (this.latestTourn.playerCount != count)
          this.updatePlayerCount(count);
      }
    }
  }

  // Winner Photo
  tournamentFinished$ = new Subject<void>();
  openShareCard$ = new Subject<void>();
  winnerPhotoBase64: string | null = null;

  async uploadWinnerPhoto(base64: string): Promise<string> {
    const url = await this.storageServ.uploadDataUrl(
      `${this.clubServ.clubID}/tournaments/${this.tournID}/winnerPhoto`,
      base64,
    );

    const update: any = {};
    update["winnerPhotoURL"] = url;
    await this.tournamentAF.update(update);

    const clubUpdate: any = {};
    clubUpdate["tournaments/" + this.tournID + "/winnerPhotoURL"] = url;
    await this.clubServ.clubAF.update(clubUpdate);

    this.latestTourn.winnerPhotoURL = url;
    this.winnerPhotoBase64 = base64;
    return url;
  }

  async deleteWinnerPhoto(): Promise<void> {
    await this.storageServ.delete(
      `${this.clubServ.clubID}/tournaments/${this.tournID}/winnerPhoto`,
    );

    const update: any = {};
    update["winnerPhotoURL"] = null;
    await this.tournamentAF.update(update);

    const clubUpdate: any = {};
    clubUpdate["tournaments/" + this.tournID + "/winnerPhotoURL"] = null;
    await this.clubServ.clubAF.update(clubUpdate);

    this.latestTourn.winnerPhotoURL = null;
    this.winnerPhotoBase64 = null;
  }

  // --- Lobby helpers (used by both card and table views) ---

  isRegisteredForTournament(tournID: string): any {
    if (this.profServ.tournaments && this.profServ.tournaments[tournID])
      return this.profServ.tournaments[tournID].regStatus;
    return false;
  }

  isFinishedInTournament(tournID: string): any {
    if (this.profServ.tournaments && this.profServ.tournaments[tournID])
      return this.profServ.tournaments[tournID].finish;
    return false;
  }

  registerForTournament(tournID: string): void {
    this.clubServ.loadingID = tournID;
    this.api.tournamentRegister(tournID, this.profServ.uid!).then(() => {
      this.clubServ.loadingID = "";
    });
  }

  unregisterFromTournament(tournID: string): void {
    this.clubServ.loadingID = tournID;
    this.api.tournamentUnregister(tournID, this.profServ.uid!).then(() => {
      this.clubServ.loadingID = "";
    });
  }

  confirmDeleteTournament(tournID: string, tournName: string): void {
    this.translate
      .get(["CONFIRM_DELETE", "CONFIRM", "CANCEL"])
      .subscribe((res: any) => {
        this.dialog
          .open(ConfirmDialogComponent, {
            data: {
              message: tournName,
              title: res["CONFIRM_DELETE"],
              acceptButton: res["CONFIRM"],
              cancelButton: res["CANCEL"],
            },
            panelClass: "confirm-dialog-container",
          })
          .afterClosed()
          .subscribe((accept: boolean) => {
            if (accept) this.clubServ.deleteTournament(tournID);
          });
      });
  }

  confirmMarkIncomplete(tournID: string, tournName: string): void {
    this.translate
      .get(["MARK_INCOMPLETE", "MARK_INCOMPLETE_CONFIRM", "CONFIRM", "CANCEL"])
      .subscribe((res: any) => {
        this.dialog
          .open(ConfirmDialogComponent, {
            data: {
              message: res["MARK_INCOMPLETE_CONFIRM"] + "\n" + tournName,
              title: res["MARK_INCOMPLETE"],
              acceptButton: res["CONFIRM"],
              cancelButton: res["CANCEL"],
            },
            panelClass: "confirm-dialog-container",
          })
          .afterClosed()
          .subscribe((accept: boolean) => {
            if (accept) this.clubServ.markIncomplete(tournID);
          });
      });
  }
}
